import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EuProjectVeld from '../../ui-components/EuProjectVeld';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Menu,
  Slider,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import TuneIcon from '@mui/icons-material/Tune';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { hasOwnCaptionPos, setCapPos, captionPosTargets } from './captionPos';

const RITMO_API_BASE = `${BACKEND_URL}/Api/ritmo/public`;

// MP4/ProRes-export: aan (render-worker draait op FFC14 v1.0.3). De knoppen staan als
// directe Stack-children (geen Fragment — dat blankte eerder de pagina).
const RITMO_VIDEO_EXPORT_ENABLED = true;

const DEFAULT_STYLE = {
  template: 'pill-vast',
  fontFamily: 'Inter',
  fontSize: 48,
  uppercase: false,
  textColor: '#ffffff',
  activeColor: '#67e8c9',
  backgroundColor: 'rgba(8, 10, 18, 0.72)',
  wordsPerLine: 5,
  // Ondertitel Studio Fase 1:
  mode: 'ritme',          // 'ritme' = woord-voor-woord karaoke · 'standaard' = rustige regels
  position: 'center',     // 'lower-third' | 'center' | 'top'
  aspectRatio: 'source',  // 'source' | '16:9' | '1:1' | '9:16' | '4:5'
  // Ondertitel Studio Fase 2 — volledige opmaak (live op de preview):
  bold: false, italic: false, underline: false,
  pastColor: '#ffffff',   // kleur van reeds gesproken woorden
  emphColor: '#ff3b30',   // kleur van benadrukte woorden
  posX: 50,               // horizontale positie (%)
  posY: 82,               // verticale positie (%) — overschrijft de positie-preset
  lineGap: 15,            // regelafstand (% van fontgrootte)
  wordGap: 28,            // woordafstand (% van fontgrootte)
  stroke: 0,              // rand-dikte (px)
  strokeColor: '#000000',
  emphScale: 122,         // nadruk-grootte (%)
  noMotion: false,        // geen pop/beweging
  boxOn: true,            // achtergrond-box
  boxColor: '#000000', boxOpacity: 60, boxRadius: 18, boxPad: 20,
  shadow: 40, glow: 0,    // schaduw/gloed-sterkte (%)
  shadowColor: '#000000', glowColor: '#ffe600',
  // Fase 3 — ondertitel-timing (inloop vóór / uitloop ná de gesproken woorden, in s):
  lead: 0.12, tail: 0,
};

// #rrggbb (of rgba(...)) → rgba-string met dekking a (0..1).
function hexToRgba(hex, a = 1) {
  if (!hex) return `rgba(0,0,0,${a})`;
  if (hex.startsWith('rgb')) return hex;
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(v, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// calm: true = rustige templates (vooraan; karaoke wordt een bewuste keuze)
const TEMPLATES = [
  { id: 'clean', name: 'Clean', kind: 'Rustig', calm: true, activeColor: '#ffffff', backgroundColor: 'transparent' },
  { id: 'clean-fill', name: 'Clean Fill', kind: 'Rustig', calm: true, activeColor: '#facc15', backgroundColor: 'transparent' },
  { id: 'pop', name: 'Pop', kind: 'Karaoke', activeColor: '#67e8c9', backgroundColor: 'transparent' },
  { id: 'pill', name: 'Pill', kind: 'Box', activeColor: '#67e8c9', backgroundColor: 'rgba(8, 10, 18, 0.72)' },
  { id: 'pill-vast', name: 'Pill Vast', kind: 'Box', activeColor: '#67e8c9', backgroundColor: 'rgba(8, 10, 18, 0.78)' },
  { id: 'highlight', name: 'Highlighter', kind: 'Box', activeColor: '#111827', backgroundColor: 'rgba(250, 204, 21, 0.88)' },
];

// Aspect-ratio-presets — zetten zinnige defaults voor fontgrootte/positie/woorden-per-regel
// per doelformaat, zodat Ritme ook voor broadcast/corporate werkt (niet alleen verticaal social).
const ASPECT_PRESETS = {
  'source':  { label: 'Bron', ratio: null },
  '16:9':    { label: '16:9', ratio: 16 / 9, fontSize: 36, wordsPerLine: 7, position: 'lower-third' },
  '1:1':     { label: '1:1', ratio: 1, fontSize: 44, wordsPerLine: 5, position: 'center' },
  '9:16':    { label: '9:16', ratio: 9 / 16, fontSize: 48, wordsPerLine: 4, position: 'center' },
  '4:5':     { label: '4:5', ratio: 4 / 5, fontSize: 46, wordsPerLine: 5, position: 'center' },
};

const POSITION_PRESETS = [
  { id: 'lower-third', label: 'Lower-third' },
  { id: 'center', label: 'Gecentreerd' },
  { id: 'top', label: 'Bovenin' },
];

// Merk-presets (huisstijl) — vaste kleuren zodat ondertitels meteen on-brand zijn.
const BRAND_PRESETS = [
  { id: 'fullframe', name: 'Full Frame', textColor: '#ffffff', activeColor: '#a855f7', backgroundColor: 'transparent' },
  { id: 'fullframe-fill', name: 'Full Frame Fill', textColor: '#ffffff', activeColor: '#ffffff', backgroundColor: 'rgba(99, 89, 131, 0.82)' },
];

// Beschikbare lettertypes. De Google-fonts worden in de browser geladen voor de
// preview; eigen/Adobe-fonts kunnen er via de UI bij (kit-id of fontnaam).
const FONT_OPTIONS = [
  { name: 'Inter', google: 'Inter:wght@400;600;700;800;900' },
  { name: 'Roboto', google: 'Roboto:wght@400;500;700;900' },
  { name: 'Montserrat', google: 'Montserrat:wght@400;600;700;800;900' },
  { name: 'Poppins', google: 'Poppins:wght@400;600;700;800' },
  { name: 'Oswald', google: 'Oswald:wght@400;500;600;700' },
  { name: 'Bebas Neue', google: 'Bebas+Neue' },
  { name: 'Anton', google: 'Anton' },
  { name: 'Archivo Black', google: 'Archivo+Black' },
  { name: 'Barlow', google: 'Barlow:wght@400;600;700;800' },
  { name: 'Rubik', google: 'Rubik:wght@400;600;700;800' },
  { name: 'Arial', google: null },
  { name: 'Helvetica', google: null },
  { name: 'Georgia', google: null },
  { name: 'Impact', google: null },
];

// Verticale positie van de ondertitel in de preview, per positie-preset.
const POSITION_STYLE = {
  'lower-third': { bottom: '8%', top: 'auto' },
  'center': { bottom: '11%', top: 'auto' },   // bestaande Ritmo-positie (iets onder midden)
  'top': { top: '8%', bottom: 'auto' },
};

function formatClock(seconds = 0) {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const mins = Math.floor(safe / 60);
  const secs = (safe % 60).toFixed(2).padStart(5, '0');
  return `${mins}:${secs}`;
}

function shortFileSize(bytes = 0) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

function stripExtension(name = '') {
  return name.replace(/\.[^.]+$/, '') || 'Naamloos project';
}

function mediaUrlForUpload(name) {
  return name ? `${RITMO_API_BASE}/media/${encodeURIComponent(name)}` : '';
}

function probeVideo(file, objectUrl) {
  return new Promise(resolve => {
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => {
      resolve({
        width: probe.videoWidth || 1920,
        height: probe.videoHeight || 1080,
        fps: 25,
        duration: Number.isFinite(probe.duration) ? probe.duration : 0,
      });
    };
    probe.onerror = () => {
      resolve({ width: 1920, height: 1080, fps: 25, duration: 0 });
    };
    probe.src = objectUrl;
  });
}

function normalizeWords(words = []) {
  return words
    .map(word => ({
      text: String(word.text || word.word || '').trim(),
      start: Number(word.start),
      end: Number(word.end),
      // prob = spraakherkenning-zekerheid (0..1); bewaar voor "onzekere woorden markeren".
      prob: typeof word.prob === 'number' ? word.prob : (typeof word.probability === 'number' ? word.probability : undefined),
      // Per-woord opmaak behouden (nadruk/cursief/onderstreept/regeleinde).
      ...(word.emph ? { emph: true } : {}),
      ...(word.italic ? { italic: true } : {}),
      ...(word.underline ? { underline: true } : {}),
      ...(word.br ? { br: true } : {}),
    }))
    .filter(word => word.text && Number.isFinite(word.start) && Number.isFinite(word.end))
    .sort((a, b) => a.start - b.start);
}
// Onzeker woord = lage herkenning-zekerheid (drempel 0.5), als markeren aanstaat.
const LOWCONF_T = 0.5;
function isLowConf(w, on) { return on && w && typeof w.prob === 'number' && w.prob < LOWCONF_T; }

// Mini-voorbeeld per template: render één frame via de echte render-engine
// (RITMO.renderCaption) zodat elke kaart de stijl live laat zien (zoals de OG).
const MINI_CAPTION = [{
  words: [
    { start: 0.0, end: 0.55, text: 'Ritme' },
    { start: 0.55, end: 1.15, text: 'in' },
    { start: 1.15, end: 1.9, text: 'beeld' },
  ],
}];
function TemplateMiniPreview({ styleId, font, ready }) {
  const wrapRef = useRef(null);
  const hostRef = useRef(null);
  useEffect(() => {
    const wrap = wrapRef.current, host = hostRef.current;
    if (!ready || !window.RITMO?.renderCaption || !wrap || !host) return;
    const W = 600, H = 338;   // 16:9 mini-canvas (referentie-resolutie)
    const project = {
      video: { width: W, height: H },
      styleId,
      font: font || 'Inter',
      captions: MINI_CAPTION,
      // Minimale overrides: laat elke stijl z'n EIGEN defaults (kleuren/motion) tonen.
      // fontSize hoog omdat de engine met H/1080 schaalt én de kaart nog eens verkleint.
      overrides: { fontSize: 200, posX: 0.5, posY: 0.5, motion: true, lineMaxWidth: 0.92 },
    };
    const draw = () => {
      const w = wrap.clientWidth || 150;
      host.style.width = W + 'px';
      host.style.height = H + 'px';
      host.style.transform = `scale(${w / W})`;
      try { window.RITMO.renderCaption(host, project, 0.85, {}); } catch { /* stil */ }
    };
    // Eén keer meten na layout; her-render bij resize van de kaart.
    const raf = requestAnimationFrame(draw);
    const ro = new ResizeObserver(draw);
    ro.observe(wrap);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [styleId, font, ready]);
  return (
    <Box ref={wrapRef} sx={{ position: 'relative', height: 84, borderRadius: 1, bgcolor: '#0b1220', overflow: 'hidden', mb: 1 }}>
      <Box ref={hostRef} sx={{ position: 'absolute', top: 0, left: 0, transformOrigin: 'top left', pointerEvents: 'none' }} />
    </Box>
  );
}

// Ondertitel-interpunctie (feedback Pepijn): geen "…"/"..." en geen punt aan het
// eind van een zin in beeld — komma's, dubbele punten, vraag- en uitroeptekens
// blijven staan. De punt wordt hiervóór wél gebruikt om zinnen op regels te splitsen.
function stripSubtitleDots(text) {
  return String(text || '')
    .replace(/^(?:\.{2,}|…)+/, '')
    .replace(/(?:\.{2,}|…|\.)+$/, '');
}

// SRT-bestand → platte woorden-array met per-woord timing. Elke cue-tijd wordt over de
// woorden verdeeld naar rato van woordlengte (zodat de karaoke-timing redelijk klopt).
function parseSrtTime(s) {
  const m = String(s).trim().match(/(\d{1,2}):(\d{2}):(\d{2})[,.](\d{1,3})/);
  if (!m) return null;
  return (+m[1]) * 3600 + (+m[2]) * 60 + (+m[3]) + (+m[4].padEnd(3, '0')) / 1000;
}
function parseSrt(text) {
  const words = [];
  const blocks = String(text).replace(/\r/g, '').split(/\n\s*\n+/);
  for (const b of blocks) {
    const lines = b.split('\n').map((l) => l.trimEnd());
    const tcLine = lines.find((l) => l.includes('-->'));
    if (!tcLine) continue;
    const [a, z] = tcLine.split('-->');
    const start = parseSrtTime(a), end = parseSrtTime(z);
    if (start == null || end == null || end <= start) continue;
    const cueText = lines
      .filter((l) => l !== tcLine && l.trim() && !/^\d+$/.test(l.trim()))
      .join(' ').replace(/<[^>]+>/g, '').replace(/\{[^}]+\}/g, '').trim();
    const toks = cueText.split(/\s+/).filter(Boolean);
    if (!toks.length) continue;
    const totalLen = toks.reduce((s, w) => s + w.length, 0) || toks.length;
    let cursor = start;
    toks.forEach((tok, i) => {
      const frac = (tok.length || 1) / totalLen;
      const wEnd = i === toks.length - 1 ? end : Math.min(end, cursor + (end - start) * frac);
      words.push({ text: tok, start: cursor, end: Math.max(cursor + 0.05, wEnd) });
      cursor = wEnd;
    });
  }
  return words;
}

function buildCaptions(words = [], wordsPerLine = 5) {
  const normalized = normalizeWords(words);
  const lines = [];
  let current = [];

  const flush = () => {
    if (!current.length) return;
    // Eerst splitsen op de punt (hierboven), daarna puntjes/eindpunten uit beeld halen.
    const cleaned = current
      .map(item => ({ ...item, text: stripSubtitleDots(item.text) }))
      .filter(item => item.text);
    if (cleaned.length) {
      lines.push({
        start: cleaned[0].start,
        end: cleaned[cleaned.length - 1].end,
        words: cleaned,
        text: cleaned.map(item => item.text).join(' '),
      });
    }
    current = [];
  };

  normalized.forEach(word => {
    current.push(word);
    const sentenceBreak = /[.!?]$/.test(word.text);
    if (current.length >= wordsPerLine || sentenceBreak) flush();
  });
  flush();

  return lines;
}

function projectFromState({ projectId, projectName, uploadName, videoMeta, captions, style }) {
  return {
    id: projectId,
    name: projectName,
    uploadName,
    video: videoMeta,
    captions,
    style,
    options: { wordsPerLine: style.wordsPerLine },
    updatedAt: new Date().toISOString(),
  };
}

// Veilige-zone marges per platform ÉN per beeldverhouding (als % van het frame) —
// exact overgenomen uit de originele RITMO (app.js). Volgorde: top/right/bottom/left.
const SAFE_LABELS = { tiktok: 'TikTok', instagram: 'Instagram Reels', youtube: 'YouTube Shorts', facebook: 'Facebook', linkedin: 'LinkedIn' };
const SAFE_ZONES = {
  // 9:16-waarden = exact de originele RITMO-safe-zones (verticaal); andere ratio's
  // zijn afgeleide, conservatievere marges.
  tiktok: {
    '9x16': { top: 6, right: 13, bottom: 22, left: 4 },
    '4x5': { top: 5, right: 6, bottom: 14, left: 4 },
    '1x1': { top: 5, right: 6, bottom: 12, left: 4 },
    '16x9': { top: 6, right: 6, bottom: 14, left: 5 },
  },
  instagram: {
    '9x16': { top: 10, right: 13, bottom: 20, left: 4 },
    '4x5': { top: 6, right: 4, bottom: 12, left: 4 },
    '1x1': { top: 6, right: 4, bottom: 12, left: 4 },
    '16x9': { top: 6, right: 4, bottom: 12, left: 4 },
  },
  youtube: {
    '9x16': { top: 7, right: 13, bottom: 13, left: 4 },
    '4x5': { top: 5, right: 5, bottom: 12, left: 5 },
    '1x1': { top: 5, right: 5, bottom: 12, left: 5 },
    '16x9': { top: 5, right: 5, bottom: 12, left: 5 },
  },
  facebook: {
    '9x16': { top: 7, right: 13, bottom: 20, left: 4 },
    '4x5': { top: 6, right: 5, bottom: 14, left: 5 },
    '1x1': { top: 6, right: 5, bottom: 14, left: 5 },
    '16x9': { top: 6, right: 5, bottom: 14, left: 5 },
  },
  linkedin: {
    '9x16': { top: 5, right: 6, bottom: 12, left: 4 },
    '4x5': { top: 5, right: 5, bottom: 10, left: 5 },
    '1x1': { top: 5, right: 5, bottom: 10, left: 5 },
    '16x9': { top: 5, right: 5, bottom: 10, left: 5 },
  },
};
const BUCKET_LABEL = { '9x16': '9:16', '4x5': '4:5', '1x1': '1:1', '16x9': '16:9' };
function aspectBucket(meta) {
  if (!meta || !meta.width || !meta.height) return '9x16';
  const r = meta.width / meta.height;
  if (r <= 0.62) return '9x16';
  if (r <= 0.9) return '4x5';
  if (r <= 1.2) return '1x1';
  return '16x9';
}

// Opmaak-schuif (module-level i.v.m. remount-bug). Live → onChange.
function StudioSlider({ label, value, min, max, step = 1, suffix = '', onChange }) {
  // Numeriek invoerveld naast de slider: klik op het getal en typ een exacte waarde.
  const clamp = (n) => Math.max(min, Math.min(max, n));
  return (
    <Box sx={{ mb: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: -0.25 }}>
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{label}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Box component="input" type="number" value={value} min={min} max={max} step={step}
            onChange={(e) => { const n = Number(e.target.value); if (!Number.isNaN(n)) onChange(clamp(n)); }}
            sx={{ width: 52, textAlign: 'right', bgcolor: '#0f1420', color: '#e5e7eb', fontFamily: 'monospace',
              fontSize: 12, border: '1px solid #2b3445', borderRadius: 1, px: 0.5, py: 0.25,
              '&:focus': { outline: 'none', borderColor: '#67e8c9' } }} />
          {suffix && <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace' }}>{suffix}</Typography>}
        </Stack>
      </Stack>
      <Slider size="small" value={value} min={min} max={max} step={step}
        onChange={(_e, v) => onChange(v)} sx={{ color: '#67e8c9', py: 1 }} />
    </Box>
  );
}
function StudioColor({ label, value, onChange }) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center' }}>
      <Box component="input" type="color" value={(value || '#000000').slice(0, 7)} onChange={(e) => onChange(e.target.value)}
        sx={{ width: '100%', height: 30, border: '1px solid #2b3445', borderRadius: 1, bgcolor: 'transparent', cursor: 'pointer', p: 0.25 }} />
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: 10 }}>{label}</Typography>
    </Box>
  );
}

export default function Ritmo() {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const pollTimerRef = useRef(null);
  const selectedFileRef = useRef(null);
  const uploadNameRef = useRef('');
  const uploadTokenRef = useRef('');

  const [projectId, setProjectId] = useState(null);
  // EU-verwerking (besluit 1B): optioneel klantproject; bij een EU-klant transcriptie/spelling alleen in de EU
  const [klantProject, setKlantProject] = useState('');
  const [projectName, setProjectName] = useState('Naamloos project');
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [videoMeta, setVideoMeta] = useState(null);
  const [captions, setCaptions] = useState([]);
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [renderJob, setRenderJob] = useState(null);      // MP4/ProRes render-job (render-server)
  const [renderJobId, setRenderJobId] = useState(null);
  const renderPollRef = useRef(null);
  const [rightTab, setRightTab] = useState('templates');
  const [tplCat, setTplCat] = useState('Alle');     // template-categoriefilter (zoals OG)
  const [tplFavs, setTplFavs] = useState(() => {     // favoriete templates (★), persistent
    try { return new Set(JSON.parse(window.localStorage.getItem('ondertitelstudio_tpl_favs') || '[]')); }
    catch { return new Set(); }
  });
  const toggleTplFav = useCallback((id) => {
    setTplFavs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { window.localStorage.setItem('ondertitelstudio_tpl_favs', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const [workerStatus, setWorkerStatus] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [collapsedClients, setCollapsedClients] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [uploadError, setUploadError] = useState('');
  const [editingCaption, setEditingCaption] = useState(null); // index van caption die bewerkt wordt
  const [editDraft, setEditDraft] = useState('');
  const [showHint, setShowHint] = useState(false); // eenmalige uitleg-hint (localStorage)
  // Fase 1: veilige zone, viewer-zoom, handboek + undo/redo-historie.
  const [safeZone, setSafeZone] = useState('none');
  // Aanpasbare tijdlijn-hoogte (item 9): sleep de splitter; de viewer schaalt mee.
  const [timelineHeight, setTimelineHeight] = useState(() => {
    const v = Number(window.localStorage?.getItem('ondertitelstudio_tlh'));
    return Number.isFinite(v) && v >= 140 ? v : 210;
  });
  const startTimelineResize = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = timelineHeight;
    const onMove = (ev) => {
      const h = Math.max(140, Math.min(window.innerHeight - 220, startH + (startY - ev.clientY)));
      setTimelineHeight(h);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setTimelineHeight(h => { try { window.localStorage.setItem('ondertitelstudio_tlh', String(h)); } catch { /* ignore */ } return h; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [timelineHeight]);
  const [viewZoom, setViewZoom] = useState('fit');   // 'fit'(=passend) | '0.5' | '1.5' | '2' | 'native'
  const previewBoxRef = useRef(null);
  const previewScrollRef = useRef(null);   // scroll-container v/d (ingezoomde) preview → hand-tool pan
  // Hand-tool: sleep om door een ingezoomd voorbeeld te schuiven (pannen).
  const startPreviewPan = useCallback((event) => {
    const sc = previewScrollRef.current;
    if (!sc || (sc.scrollWidth <= sc.clientWidth && sc.scrollHeight <= sc.clientHeight)) return;
    if (event.button !== 0) return;
    event.preventDefault();
    const startX = event.clientX, startY = event.clientY;
    const sl = sc.scrollLeft, st = sc.scrollTop;
    sc.style.cursor = 'grabbing';
    const onMove = (e) => { sc.scrollLeft = sl - (e.clientX - startX); sc.scrollTop = st - (e.clientY - startY); };
    const onUp = () => { sc.style.cursor = ''; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);
  // Meet de beschikbare preview-ruimte (de cel, exclusief padding). Reageert ook op
  // een hogere/lagere tijdlijn (dan verandert deze cel).
  const [cell, setCell] = useState({ w: 0, h: 0 });
  useEffect(() => {
    // Let op: pas als er een video is bestaat de preview-container. Daarom hangt dit
    // effect aan de media-URL/timelineHeight — anders is previewScrollRef.current null
    // op mount en wordt de ResizeObserver nooit gekoppeld (→ fitDims blijft leeg → fout).
    // (hasVideo is verderop gedefinieerd; hier direct de bron-state gebruiken.)
    const hasMedia = Boolean(mediaUrl || previewUrl);
    if (!hasMedia) return undefined;
    const el = previewScrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const cs = window.getComputedStyle(el);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      setCell({ w: Math.max(0, el.clientWidth - padX), h: Math.max(0, el.clientHeight - padY) });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [mediaUrl, previewUrl, timelineHeight]);
  // Bereken de "passend"-afmetingen zelf (contain-fit) i.p.v. via CSS aspect-ratio +
  // max-height — die combinatie clipt onbetrouwbaar bij staande (portrait) video's.
  const fitDims = (() => {
    const W = videoMeta?.width || 1080, H = videoMeta?.height || 1920;
    if (!cell.w || !cell.h || !W || !H) return null;
    const scale = Math.min(cell.w / W, cell.h / H);
    return { w: Math.max(1, Math.round(W * scale)), h: Math.max(1, Math.round(H * scale)) };
  })();
  // Schaalfactor t.o.v. de "passend"-weergave. Fit = 1; 1:1 = native pixels.
  const zoomScale = viewZoom === 'fit'
    ? 1
    : viewZoom === 'native'
      ? (videoMeta?.width && fitDims?.w ? Math.max(0.1, videoMeta.width / fitDims.w) : 1)
      : Number(viewZoom) || 1;
  const [showHelp, setShowHelp] = useState(false);
  const [markLowConf, setMarkLowConf] = useState(true); // onzekere woorden markeren
  // Fase 4 — export-opties (worden als job-velden naar de render-server gestuurd).
  const [exportFast, setExportFast] = useState(false);   // snelle render (lagere res)
  const [exportScale, setExportScale] = useState('');    // langste zijde: '' = origineel
  const [exportRangeOnly, setExportRangeOnly] = useState(false); // alleen in/uit-fragment
  const [mp4Preset, setMp4Preset] = useState('youtube'); // youtube | instagram | tiktok
  const [outputDir, setOutputDir] = useState(() => { try { return window.localStorage.getItem('ondertitelstudio_outdir') || ''; } catch { return ''; } });
  const [subEllipsis, setSubEllipsis] = useState('end_only'); // puntjes bij zinsafbreking (SRT/VTT)
  const histRef = useRef({ stack: [], pointer: -1, applying: false });
  // Fase 1b: tijdlijn-zoom, in-/uit-punt (fragment), hand-tool, waveform.
  const [tlZoom, setTlZoom] = useState(1);            // 1 = basis; breder = ingezoomd
  const [inPoint, setInPoint] = useState(null);
  const [outPoint, setOutPoint] = useState(null);
  const [handTool, setHandTool] = useState(false);
  const [wavePeaks, setWavePeaks] = useState(null);   // genormaliseerde peaks [0..1]
  const tlScrollRef = useRef(null);
  const waveCanvasRef = useRef(null);
  // Fase 2 (parity): de gedeelde karaoke.js-renderer voor de preview = export.
  // ── Lettertypes (item 12): Google-fonts laden + eigen/Adobe-fonts + beschikbaarheidscheck ──
  const [customFonts, setCustomFonts] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('ondertitelstudio_fonts') || '[]'); } catch { return []; }
  });
  const [adobeKit, setAdobeKit] = useState(() => {
    try { return window.localStorage.getItem('ondertitelstudio_adobe_kit') || ''; } catch { return ''; }
  });
  const [fontTick, setFontTick] = useState(0); // her-render zodra fonts geladen zijn
  // Geüploade fonts (huisstijl/klant, .ttf/.otf op de server): als FontFace in de browser
  // laden zodat preview = render (de render-worker installeert dezelfde bestanden).
  const [uploadedFonts, setUploadedFonts] = useState([]); // [{ name, filename }]
  const fontUploadRef = useRef(null);
  const loadUploadedFonts = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/srt-generator/fonts`);
      if (!r.ok) return;
      const d = await r.json();
      const lijst = Array.isArray(d.customFonts) ? d.customFonts : [];
      setUploadedFonts(lijst);
      for (const f of lijst) {
        if (!f?.name || !f?.filename) continue;
        try {
          if (document.fonts?.check?.(`16px "${f.name}"`)) continue;
          const rf = await authFetch(`${BACKEND_URL}/Api/srt-generator/fonts/${encodeURIComponent(f.filename)}`);
          if (!rf.ok) continue;
          const face = new FontFace(f.name, await rf.arrayBuffer());
          await face.load();
          document.fonts.add(face);
        } catch { /* font blijft systeemfallback */ }
      }
      setFontTick(t => t + 1);
    } catch { /* geen lijst — dan alleen Google/Adobe */ }
  }, []);
  useEffect(() => { loadUploadedFonts(); }, [loadUploadedFonts]);
  const uploadFont = useCallback(async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('font', file);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/srt-generator/fonts/upload`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      await loadUploadedFonts();
      if (d.name) setStyle(prev => ({ ...prev, fontFamily: d.name }));
      showNotice(`Font "${d.name || file.name}" geüpload — beschikbaar in preview én render`, 'success');
    } catch (e) { showNotice(`Font uploaden mislukt: ${e.message}`, 'error'); }
  }, [loadUploadedFonts]);
  useEffect(() => {
    if (!document.querySelector('link[data-ritmo-fonts]')) {
      const families = FONT_OPTIONS.filter(f => f.google).map(f => `family=${f.google}`).join('&');
      const l = document.createElement('link');
      l.rel = 'stylesheet'; l.dataset.ritmoFonts = '1';
      l.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
      document.head.appendChild(l);
    }
    document.fonts?.ready?.then(() => setFontTick(t => t + 1));
  }, []);
  useEffect(() => {
    if (!adobeKit) return;
    const id = `ritmo-typekit-${adobeKit}`;
    if (!document.getElementById(id)) {
      const l = document.createElement('link');
      l.id = id; l.rel = 'stylesheet'; l.href = `https://use.typekit.net/${adobeKit}.css`;
      document.head.appendChild(l);
    }
    document.fonts?.ready?.then(() => setFontTick(t => t + 1));
    try { window.localStorage.setItem('ondertitelstudio_adobe_kit', adobeKit); } catch { /* ignore */ }
  }, [adobeKit]);
  // Best-effort: is dit font beschikbaar in de browser? (fontTick forceert hercheck)
  const fontAvailable = useCallback((name) => {
    void fontTick;
    try { return !document.fonts || document.fonts.check(`16px "${name}"`); } catch { return true; }
  }, [fontTick]);
  const addCustomFont = useCallback((raw) => {
    const name = (raw || '').trim();
    if (!name) return;
    setCustomFonts(prev => {
      const next = Array.from(new Set([...prev, name]));
      try { window.localStorage.setItem('ondertitelstudio_fonts', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setStyle(prev => ({ ...prev, fontFamily: name }));
  }, []);
  // "+ Eigen font": zoekveld met suggesties i.p.v. de exacte naam uit het hoofd typen.
  // Fonts van déze Mac via de Local Font Access API (Chrome/Edge, vraagt eenmalig
  // toestemming); die moet binnen de klik worden aangeroepen, dus hier en niet in een effect.
  const [fontDialogOpen, setFontDialogOpen] = useState(false);
  const [fontQuery, setFontQuery] = useState('');
  const [localFontFamilies, setLocalFontFamilies] = useState(null); // null = (nog) niet beschikbaar
  const openFontDialog = useCallback(async () => {
    setFontQuery('');
    setFontDialogOpen(true);
    if (localFontFamilies || typeof window.queryLocalFonts !== 'function') return;
    try {
      const fonts = await window.queryLocalFonts();
      setLocalFontFamilies(Array.from(new Set(fonts.map(f => f.family))).sort((a, b) => a.localeCompare(b)));
    } catch { /* toestemming geweigerd → alleen bekende fonts als suggestie */ }
  }, [localFontFamilies]);
  const fontSuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (name, group) => { if (name && !seen.has(name)) { seen.add(name); out.push({ name, group }); } };
    uploadedFonts.forEach(f => push(f.name, 'Geüpload (huisstijl/klant)'));
    customFonts.forEach(n => push(n, 'Eerder toegevoegd'));
    FONT_OPTIONS.forEach(f => push(f.name, 'Standaard'));
    (localFontFamilies || []).forEach(n => push(n, 'Op deze Mac'));
    return out;
  }, [uploadedFonts, customFonts, localFontFamilies]);
  const confirmFontDialog = useCallback(() => {
    if (!fontQuery.trim()) return;
    addCustomFont(fontQuery);
    setFontDialogOpen(false);
  }, [fontQuery, addCustomFont]);

  // ── Eigen opmaak-templates (item 16): opslaan per klant/naam, weer oproepbaar ──
  const [savedTemplates, setSavedTemplates] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('ondertitelstudio_styles') || '[]'); } catch { return []; }
  });
  const persistTemplates = (list) => {
    setSavedTemplates(list);
    try { window.localStorage.setItem('ondertitelstudio_styles', JSON.stringify(list)); } catch { /* ignore */ }
  };

  const [ritmoReady, setRitmoReady] = useState(false);
  const stageRef = useRef(null);
  useEffect(() => {
    if (window.RITMO?.renderCaption) { setRitmoReady(true); return; }
    const onload = () => setRitmoReady(Boolean(window.RITMO?.renderCaption));
    const existing = document.querySelector('script[data-ritmo-karaoke]');
    if (existing) { existing.addEventListener('load', onload); return; }
    const s = document.createElement('script');
    s.src = `${process.env.PUBLIC_URL || ''}/render/karaoke.js`;
    s.async = true; s.dataset.ritmoKaraoke = '1';
    s.onload = onload; s.onerror = () => setRitmoReady(false);
    document.body.appendChild(s);
  }, []);
  const tlTrackRef = useRef(null);
  const tlRulerRef = useRef(null);   // tijd-liniaal (ticks + timestamps) boven de track
  // Fase 1c: per-woord bewerken op de tijdlijn.
  const [selWord, setSelWord] = useState(null);       // { ci, wi } | null

  // 1e-keer-hint: leg de Standaard/Ritme-keuze één keer uit, daarna nooit meer.
  useEffect(() => {
    try {
      if (!window.localStorage.getItem('ondertitelstudio_hint_v1')) setShowHint(true);
    } catch { /* localStorage kan geblokkeerd zijn */ }
  }, []);

  const dismissHint = useCallback(() => {
    setShowHint(false);
    try { window.localStorage.setItem('ondertitelstudio_hint_v1', '1'); } catch { /* ignore */ }
  }, []);

  const activeCaption = useMemo(() => {
    // Inloop/uitloop: regel iets eerder/langer tonen (zelfde gedrag als de export).
    const lead = Math.max(0, style.lead ?? 0.12), tail = Math.max(0, style.tail ?? 0);
    return captions.find(caption => currentTime >= caption.start - lead && currentTime <= caption.end + tail) || null;
  }, [captions, currentTime, style.lead, style.tail]);

  const allWords = useMemo(() => captions.flatMap(caption => caption.words || []), [captions]);
  const duration = videoMeta?.duration || videoRef.current?.duration || 0;
  const hasVideo = Boolean(mediaUrl || previewUrl);
  const transcribing = Boolean(jobId && job?.status !== 'done' && job?.status !== 'error');
  const canStartTranscription = hasVideo && !transcribing && !uploading;
  // Leg uit waarom "Spraak herkennen" uitgegrijsd is (anders lijkt het een bug).
  const transcribeDisabledReason = uploading
    ? 'Bezig met uploaden…'
    : !hasVideo
      ? 'Kies of upload eerst een video'
      : transcribing
        ? 'Transcriptie loopt al'
        : '';

  const showNotice = useCallback((message, severity = 'info') => {
    setNotice({ message, severity });
  }, []);

  const setBackendUploadName = useCallback((name = '') => {
    uploadNameRef.current = name;
    setUploadName(name);
  }, []);

  const setBackendUploadToken = useCallback((token = '') => {
    uploadTokenRef.current = token;
  }, []);

  const ritmoFetch = useCallback(async (path, options = {}) => {
    const response = await authFetch(`${RITMO_API_BASE}${path}`, options);
    if (!response.ok) {
      let detail = `Ritmo API fout (${response.status})`;
      try {
        const data = await response.json();
        detail = data.detail || data.error || detail;
      } catch {
        // Keep default detail.
      }
      throw new Error(detail);
    }
    return response;
  }, []);

  const ritmoPublicFetch = useCallback(async (path, options = {}) => {
    const response = await fetch(`${RITMO_API_BASE}${path}`, options);
    if (!response.ok) {
      let detail = `Ritmo API fout (${response.status})`;
      try {
        const data = await response.json();
        detail = data.detail || data.error || detail;
      } catch {
        // Keep default detail.
      }
      throw new Error(detail);
    }
    return response;
  }, []);

  const uploadToBackend = useCallback(async (file, meta) => {
    if (!file) throw new Error('Kies eerst een video');
    setUploading(true);
    setUploadStatus('uploading');
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('meta', JSON.stringify(meta || { width: 1920, height: 1080, fps: 25, duration: 0 }));

      const uploaded = await ritmoFetch('/import', { method: 'POST', body: formData }).then(res => res.json());
      if (!uploaded.name) throw new Error('Backend gaf geen uploadnaam terug');
      setBackendUploadName(uploaded.name);
      setBackendUploadToken(uploaded.upload_token || '');
      setMediaUrl(uploaded.media_url || mediaUrlForUpload(uploaded.name));
      setVideoMeta(uploaded.video || meta);
      setUploadStatus('ready');
      return uploaded.name;
    } catch (error) {
      setUploadStatus('error');
      setUploadError(error.message);
      throw error;
    } finally {
      setUploading(false);
    }
  }, [ritmoFetch, setBackendUploadName, setBackendUploadToken]);

  const loadWorkerStatus = useCallback(async () => {
    try {
      const data = await ritmoFetch('/worker-status').then(res => res.json());
      setWorkerStatus(data);
    } catch (error) {
      setWorkerStatus({ configured: false, online: false, error: error.message });
    }
  }, [ritmoFetch]);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const data = await ritmoFetch('/projects').then(res => res.json());
      setProjects(data.projects || []);
    } catch (error) {
      showNotice(error.message, 'error');
    } finally {
      setLoadingProjects(false);
    }
  }, [ritmoFetch, showNotice]);

  useEffect(() => {
    loadWorkerStatus();
    loadProjects();
  }, [loadProjects, loadWorkerStatus]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!jobId) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const data = await ritmoPublicFetch(`/jobs/${jobId}`).then(res => res.json());
        if (cancelled) return;
        setJob(data);

        if (data.status === 'done') {
          const words = data.result?.words || [];
          const nextCaptions = buildCaptions(words, style.wordsPerLine);
          setCaptions(nextCaptions);
          setJobId(null);
          showNotice(`Transcriptie klaar: ${words.length} woorden`, 'success');
          return;
        }

        if (data.status === 'error') {
          setJobId(null);
          showNotice(data.error || data.message || 'Transcriptie mislukt', 'error');
          return;
        }

        pollTimerRef.current = window.setTimeout(poll, 1500);
      } catch (error) {
        if (!cancelled) {
          setJob(prev => ({ ...(prev || {}), status: 'error', error: error.message }));
          setJobId(null);
          showNotice(error.message, 'error');
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
    };
  }, [jobId, ritmoPublicFetch, showNotice, style.wordsPerLine]);

  const handleVideoTime = useCallback(() => {
    setCurrentTime(videoRef.current?.currentTime || 0);
  }, []);

  // Ref naar startTranscription zodat handleFile (eerder gedefinieerd) 'm kan
  // aanroepen voor auto-transcriptie na upload.
  const startTranscriptionRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setJob(null);
    setJobId(null);
    setCaptions([]);
    setProjectId(null);
    setBackendUploadName('');
    setBackendUploadToken('');
    setUploadStatus('idle');
    setUploadError('');
    selectedFileRef.current = file;

    try {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return objectUrl;
      });
      setMediaUrl(objectUrl);
      setProjectName(stripExtension(file.name));

      const meta = await probeVideo(file, objectUrl);
      setVideoMeta(meta);

      const uploadedName = await uploadToBackend(file, meta);
      showNotice(`${file.name} geimporteerd (${shortFileSize(file.size)})`, 'success');
      // Direct spraak herkennen — dat wil je (vrijwel) altijd na een upload.
      // Via ref omdat startTranscription verderop gedefinieerd is.
      startTranscriptionRef.current?.(uploadedName);
    } catch (error) {
      showNotice(`Upload naar backend mislukt: ${error.message}. Je kunt de preview wel bekijken en opnieuw proberen.`, 'error');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [setBackendUploadName, setBackendUploadToken, showNotice, uploadToBackend]);

  // Video vervangen MÉT behoud van ondertitels + styling (bv. een nieuwe render/versie
  // van dezelfde montage). Anders dan handleFile: geen reset van captions, geen
  // her-transcriptie — alleen de media (naam/url/meta) wordt vervangen.
  const replaceVideoInputRef = useRef(null);
  const replaceVideo = useCallback(async (file) => {
    if (!file) return;
    selectedFileRef.current = file;
    try {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(previous => { if (previous) URL.revokeObjectURL(previous); return objectUrl; });
      const meta = await probeVideo(file, objectUrl);
      await uploadToBackend(file, meta); // zet media-naam/url/meta; captions blijven staan
      showNotice('Video vervangen — ondertitels & styling behouden', 'success');
    } catch (error) {
      showNotice(`Video vervangen mislukt: ${error.message}`, 'error');
    } finally {
      if (replaceVideoInputRef.current) replaceVideoInputRef.current.value = '';
    }
  }, [uploadToBackend, showNotice]);

  // Template (opmaak-stijl) exporteren/importeren als los JSON-bestand — zo kun je een
  // stijl delen of backuppen los van de server-presets.
  const templateImportRef = useRef(null);
  const exportTemplate = useCallback(() => {
    const nm = projectName || 'template';
    const payload = { ritmoTemplate: 1, name: nm, style };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ritmo-template-${String(nm).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    showNotice('Template geëxporteerd (JSON)', 'success');
  }, [projectName, style, showNotice]);
  const importTemplate = useCallback(async (file) => {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const st = parsed.style || parsed.data || parsed; // {style} / {data} / kale style
      const nm = parsed.name || stripExtension(file.name);
      if (!st || typeof st !== 'object') throw new Error('geen geldige template');
      setStyle(prev => ({ ...prev, ...st }));
      persistTemplates([...savedTemplates.filter(t => t.name !== nm), { name: nm, style: st }]);
      showNotice(`Template "${nm}" geïmporteerd en toegepast`, 'success');
    } catch (e) {
      showNotice(`Template importeren mislukt: ${e.message}`, 'error');
    } finally {
      if (templateImportRef.current) templateImportRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedTemplates, style, projectName]);

  // SRT importeren → captions vullen (met woord-timing). Handig om een bestaand
  // transcript te hergebruiken i.p.v. opnieuw te laten herkennen.
  const srtImportRef = useRef(null);
  const importSrt = useCallback(async (file) => {
    if (!file) return;
    try {
      const words = parseSrt(await file.text());
      if (!words.length) throw new Error('geen ondertitels gevonden in dit .srt-bestand');
      setCaptions(buildCaptions(words, style.wordsPerLine));
      showNotice(`SRT geïmporteerd — ${words.length} woorden`, 'success');
    } catch (e) {
      showNotice(`SRT importeren mislukt: ${e.message}`, 'error');
    } finally {
      if (srtImportRef.current) srtImportRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style.wordsPerLine]);

  const handleInputChange = useCallback((event) => {
    handleFile(event.target.files?.[0]);
  }, [handleFile]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragActive(false);
    handleFile(event.dataTransfer.files?.[0]);
  }, [handleFile]);

  const startTranscription = useCallback(async (overrideName) => {
    // overrideName: direct na upload is de state nog niet rond — de uploadnaam
    // komt dan als argument mee (let op: een button-onClick geeft een event mee).
    let sourceName = (typeof overrideName === 'string' && overrideName) || uploadNameRef.current || uploadName;
    if (!sourceName && selectedFileRef.current) {
      try {
        setJob({ status: 'running', progress: 0, message: 'Video uploaden naar backend...' });
        sourceName = await uploadToBackend(selectedFileRef.current, videoMeta);
      } catch (error) {
        setJob({ status: 'error', error: error.message, progress: 1 });
        showNotice(`Upload nodig voor transcriptie, maar die mislukt: ${error.message}`, 'error');
        return;
      }
    }

    if (!sourceName) {
      showNotice('Kies eerst een video', 'warning');
      return;
    }

    try {
      setJob({ status: 'running', progress: 0, message: 'Transcriptie starten...' });
      const formData = new FormData();
      formData.append('name', sourceName);
      formData.append('language', 'auto');
      formData.append('model_size', 'small');
      if (klantProject.trim()) formData.append('project_nummer', klantProject.trim());
      if (projectId) formData.append('ritmo_project_id', projectId);
      const uploadToken = uploadTokenRef.current;
      if (uploadToken) formData.append('upload_token', uploadToken);
      const transcribeOptions = { method: 'POST', body: formData };
      const data = await (uploadToken
        ? ritmoPublicFetch(`/transcribe?upload_token=${encodeURIComponent(uploadToken)}`, transcribeOptions)
        : ritmoFetch('/transcribe', transcribeOptions)
      ).then(res => res.json());
      setJobId(data.job);
      showNotice('Transcriptie-job gestart', 'info');
    } catch (error) {
      setJob({ status: 'error', error: error.message, progress: 1 });
      showNotice(error.message, 'error');
    }
  }, [klantProject, projectId, ritmoFetch, ritmoPublicFetch, showNotice, uploadName, uploadToBackend, videoMeta]);
  useEffect(() => { startTranscriptionRef.current = startTranscription; }, [startTranscription]);

  // ── Shuttle (J/K/L) + reverse-playback-engine ────────────────────────────────
  // Browsers spelen niet native achteruit; J doet dat via een rAF-lus die currentTime
  // terugtelt. L = vooruit (herhaald = sneller), K = pauze, spatie = altijd 1×.
  const playRateRef = useRef(1);
  const shuttleRef = useRef({ raf: 0, rate: 0, last: 0 });
  const stopReverse = useCallback(() => {
    if (shuttleRef.current.raf) cancelAnimationFrame(shuttleRef.current.raf);
    shuttleRef.current.raf = 0;
    shuttleRef.current.rate = 0;
  }, []);
  const setShuttle = useCallback((rate) => {
    const v = videoRef.current;
    if (!v) return;
    stopReverse();
    if (rate > 0) {
      playRateRef.current = rate;
      v.playbackRate = rate;
      v.play?.();
    } else if (rate < 0) {
      v.pause();
      v.playbackRate = 1;
      shuttleRef.current.rate = rate;
      shuttleRef.current.last = performance.now();
      const step = (now) => {
        const s = shuttleRef.current;
        if (s.rate >= 0) return;
        const dt = (now - s.last) / 1000; s.last = now;
        const nt = Math.max(0, (v.currentTime || 0) + s.rate * dt);
        v.currentTime = nt; setCurrentTime(nt);
        if (nt <= 0) { s.rate = 0; s.raf = 0; return; }
        s.raf = requestAnimationFrame(step);
      };
      shuttleRef.current.raf = requestAnimationFrame(step);
    } else {
      playRateRef.current = 1;
      v.playbackRate = 1;
      v.pause();
    }
  }, [stopReverse]);

  const togglePlayback = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    const reversing = shuttleRef.current.rate < 0;
    stopReverse();
    try {
      if (video.paused || reversing) {
        video.playbackRate = 1; playRateRef.current = 1;   // spatie/afspeelknop = altijd 1×
        await video.play();
      } else {
        video.pause();
      }
    } catch (error) {
      showNotice(`Video kan niet afspelen: ${error.message}`, 'error');
    }
  }, [showNotice, stopReverse]);

  // Zet de afspeelcursor op een tijd (s). Gebruikt door klik/sleep op de tijdlijn.
  const seekTo = useCallback((time) => {
    const dur = videoRef.current?.duration || duration || 0;
    const clamped = Math.max(0, Math.min(time, dur || time));
    if (videoRef.current) videoRef.current.currentTime = clamped;
    setCurrentTime(clamped);
  }, [duration]);

  // Bereken de tijd uit een muis-x op een tijdlijn-element en seek ernaartoe.
  const seekFromPointer = useCallback((clientX, el) => {
    const dur = videoRef.current?.duration || duration || 0;
    if (!el || !dur) return;
    const rect = el.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    seekTo(fraction * dur);
  }, [duration, seekTo]);

  // Slepen op de tijdlijn: volg de muis tot loslaten.
  const startTimelineScrub = useCallback((event) => {
    const el = event.currentTarget;
    seekFromPointer(event.clientX, el);
    const onMove = (e) => seekFromPointer(e.clientX, el);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [seekFromPointer]);

  // Hand-tool: sleep om door de (ingezoomde) tijdlijn te pannen i.p.v. te scrubben.
  const startTimelinePan = useCallback((event) => {
    const sc = tlScrollRef.current;
    if (!sc) return;
    const startX = event.clientX;
    const startScroll = sc.scrollLeft;
    const onMove = (e) => { sc.scrollLeft = startScroll - (e.clientX - startX); };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // ── Undo/redo: snapshot van {captions, style} bij elke wijziging ──────────────
  // Coalescing: wijzigingen die snel op elkaar volgen (slepen van een woord,
  // slider, doortypen) vervangen de top van de stack i.p.v. een nieuwe entry —
  // anders ging Cmd+Z pixel-voor-pixel terug door een drag i.p.v. in één keer.
  useEffect(() => {
    const h = histRef.current;
    if (h.applying) { h.applying = false; return; }
    const now = Date.now();
    h.stack = h.stack.slice(0, h.pointer + 1);
    if (h.lastPush && (now - h.lastPush) < 500 && h.stack.length > 0) {
      h.stack[h.stack.length - 1] = { captions, style };
    } else {
      h.stack.push({ captions, style });
      if (h.stack.length > 60) h.stack.shift();
    }
    h.pointer = h.stack.length - 1;
    h.lastPush = now;
  }, [captions, style]);

  const undo = useCallback(() => {
    const h = histRef.current;
    if (h.pointer <= 0) return;
    h.pointer -= 1;
    h.applying = true;
    const snap = h.stack[h.pointer];
    setStyle(snap.style); setCaptions(snap.captions);
  }, []);
  const redo = useCallback(() => {
    const h = histRef.current;
    if (h.pointer >= h.stack.length - 1) return;
    h.pointer += 1;
    h.applying = true;
    const snap = h.stack[h.pointer];
    setStyle(snap.style); setCaptions(snap.captions);
  }, []);

  // ── Waveform: decodeer de audio van de bron client-side → genormaliseerde peaks ──
  useEffect(() => {
    const src = previewUrl || mediaUrl;
    if (!src) { setWavePeaks(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const buf = await fetch(src).then(r => r.arrayBuffer());
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const audio = await ctx.decodeAudioData(buf);
        const ch = audio.getChannelData(0);
        const BUCKETS = 1600;
        const block = Math.max(1, Math.floor(ch.length / BUCKETS));
        const peaks = new Array(BUCKETS);
        let max = 0.0001;
        for (let i = 0; i < BUCKETS; i++) {
          let peak = 0;
          const start = i * block;
          for (let j = 0; j < block; j++) { const v = Math.abs(ch[start + j] || 0); if (v > peak) peak = v; }
          peaks[i] = peak; if (peak > max) max = peak;
        }
        const norm = peaks.map(p => p / max);
        ctx.close?.();
        if (!cancelled) setWavePeaks(norm);
      } catch { if (!cancelled) setWavePeaks(null); /* geen waveform is niet erg */ }
    })();
    return () => { cancelled = true; };
  }, [previewUrl, mediaUrl]);

  // Teken de waveform op het canvas. Het canvas spant 100% van de tijdlijn-track;
  // we meten de werkelijke pixelbreedte zodat 'ie exact onder de woord-blokken (op %)
  // valt — ook bij zoom/resize. Een ResizeObserver hertekent bij elke breedte-wijziging.
  useEffect(() => {
    const cv = waveCanvasRef.current;
    if (!cv) return;
    const draw = () => {
      const ctx = cv.getContext('2d');
      const w = cv.width = Math.max(1, Math.round(cv.clientWidth || cv.offsetWidth || 900));
      const h = cv.height;
      ctx.clearRect(0, 0, w, h);
      const peaks = wavePeaks;
      if (!peaks || !peaks.length) return;
      ctx.fillStyle = 'rgba(103, 232, 201, 0.28)';
      const mid = h / 2;
      for (let x = 0; x < w; x++) {
        const p = peaks[Math.min(peaks.length - 1, Math.floor((x / w) * peaks.length))];
        const bar = Math.max(1, p * (h - 4));
        ctx.fillRect(x, mid - bar / 2, 1, bar);
      }
    };
    draw();
    const ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(draw) : null;
    ro?.observe(cv);
    return () => ro?.disconnect();
  }, [wavePeaks, tlZoom, duration]);

  const zoomTl = useCallback((factor) => setTlZoom(z => Math.max(1, Math.min(20, z * factor))), []);

  // ── Parity-preview: bouw het project dat karaoke.js (RITMO.renderCaption) verwacht ──
  const buildPreviewProject = useCallback(() => {
    // Verticale positie: vrije posY (%) heeft voorrang, anders afgeleid van de preset.
    const posY = style.posY != null
      ? style.posY / 100
      : (style.position === 'top' ? 0.18 : style.position === 'lower-third' ? 0.8 : 0.5);
    return {
      video: { width: videoMeta?.width || 1080, height: videoMeta?.height || 1920 },
      styleId: style.template,
      font: style.fontFamily,
      captions,
      overrides: {
        fontSize: style.fontSize, posX: (style.posX ?? 50) / 100, posY,
        baseColor: style.textColor, activeColor: style.activeColor,
        pastColor: style.pastColor, emphColor: style.emphColor,
        uppercase: style.uppercase, bold: style.bold, italic: style.italic, underlineText: style.underline,
        motion: !style.noMotion,
        strokeWidth: style.stroke, strokeColor: style.strokeColor,
        lineGap: (style.lineGap ?? 15) / 100, wordGap: (style.wordGap ?? 28) / 100,
        emphScale: (style.emphScale ?? 122) / 100,
        boxOn: style.boxOn, boxColor: style.boxColor, boxOpacity: (style.boxOpacity ?? 60) / 100,
        boxRadius: (style.boxRadius ?? 18) / 100, boxPad: (style.boxPad ?? 20) / 100,
        shadowStrength: (style.shadow ?? 40) / 100, shadowColor: style.shadowColor,
        glowStrength: (style.glow ?? 0) / 100, glowColor: style.glowColor,
        lead: Math.max(0, style.lead ?? 0.12), tail: Math.max(0, style.tail ?? 0),
      },
    };
  }, [style, captions, videoMeta]);

  // Render-lus: teken de preview elke frame via de gedeelde renderer (= de export).
  useEffect(() => {
    if (!ritmoReady || !hasVideo || !window.RITMO?.renderCaption) return;
    let raf, alive = true;
    const draw = () => {
      if (!alive) return;
      const stage = stageRef.current, vid = videoRef.current;
      if (stage && vid && videoMeta?.width) {
        const W = videoMeta.width, H = videoMeta.height;
        const dispW = vid.clientWidth || W;
        stage.style.width = W + 'px'; stage.style.height = H + 'px';
        stage.style.transform = `scale(${dispW / W})`;
        try { window.RITMO.renderCaption(stage, buildPreviewProject(), vid.currentTime || 0, {}); } catch { /* fallback = React */ }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [ritmoReady, hasVideo, videoMeta, buildPreviewProject]);

  // ── Per-woord bewerken op de tijdlijn ─────────────────────────────────────────
  // Houd caption.start/end/text in sync met de woorden (gesorteerd op tijd).
  const reflowCap = (cap) => {
    const ws = (cap.words || []).slice().sort((a, b) => a.start - b.start);
    if (!ws.length) return { ...cap, words: ws };
    return { ...cap, words: ws, start: Math.min(...ws.map(w => w.start)), end: Math.max(...ws.map(w => w.end)), text: ws.map(w => w.text).join(' ') };
  };
  // Muteer captions onveranderlijk; fn krijgt een diepe kloon en mag muteren.
  const mutateCaptions = useCallback((fn) => {
    setCaptions(prev => {
      const clone = prev.map(c => ({ ...c, words: (c.words || []).map(w => ({ ...w })) }));
      const out = fn(clone);
      const next = (out || clone).filter(c => (c.words || []).length).map(reflowCap);
      return next;
    });
  }, []);

  const deleteWord = useCallback((ci, wi) => { mutateCaptions(caps => { caps[ci]?.words.splice(wi, 1); }); setSelWord(null); }, [mutateCaptions]);
  // Woordblokje verslepen (tekst-tab): verplaats een woord binnen of tussen regels.
  // to.wi = invoegen vóór dat woord; to.wi == null = achteraan de doelregel.
  const dragWordRef = useRef(null);
  const moveWord = useCallback((from, to) => {
    if (!from || !to || (from.ci === to.ci && from.wi === to.wi)) return;
    mutateCaptions(caps => {
      const src = caps[from.ci]; if (!src) return;
      const [w] = src.words.splice(from.wi, 1); if (!w) return;
      let twi = to.wi;
      if (to.ci === from.ci && twi != null && twi > from.wi) twi -= 1;
      const dst = caps[to.ci]; if (!dst) { src.words.splice(from.wi, 0, w); return; }
      if (twi == null || twi > dst.words.length) dst.words.push(w);
      else dst.words.splice(twi, 0, w);
    });
    setSelWord(null);
  }, [mutateCaptions]);
  const toggleEmph = useCallback((ci, wi) => { mutateCaptions(caps => { const w = caps[ci]?.words[wi]; if (w) w.emph = !w.emph; }); }, [mutateCaptions]);
  const toggleItalic = useCallback((ci, wi) => { mutateCaptions(caps => { const w = caps[ci]?.words[wi]; if (w) w.italic = !w.italic; }); }, [mutateCaptions]);
  const toggleUnderline = useCallback((ci, wi) => { mutateCaptions(caps => { const w = caps[ci]?.words[wi]; if (w) w.underline = !w.underline; }); }, [mutateCaptions]);
  const toggleBreak = useCallback((ci, wi) => { mutateCaptions(caps => { const w = caps[ci]?.words[wi]; if (w) w.br = !w.br; }); }, [mutateCaptions]);
  const splitAtWord = useCallback((ci, wi) => {
    if (wi <= 0) return;
    mutateCaptions(caps => { const cap = caps[ci]; if (!cap || wi >= cap.words.length) return; const tail = cap.words.splice(wi); caps.splice(ci + 1, 0, { words: tail }); });
    setSelWord(null);
  }, [mutateCaptions]);
  const nudgeWord = useCallback((ci, wi, dt, adjustEnd) => {
    mutateCaptions(caps => {
      const w = caps[ci]?.words[wi]; if (!w) return;
      if (adjustEnd) { w.end = Math.max(w.start + 0.05, w.end + dt); }
      else { const len = w.end - w.start; w.start = Math.max(0, w.start + dt); w.end = w.start + len; }
    });
  }, [mutateCaptions]);
  // Woord naar begin/einde van de regel (timing + volgorde) — uit app.js.
  // De selectie volgt het woord naar z'n nieuwe plek, zodat herhaald klikken werkt.
  const moveWordToEdge = useCallback((ci, wi, toStart) => {
    const len = captions[ci]?.words?.length ?? 0;
    if (len < 2 || !captions[ci]?.words?.[wi]) return;
    mutateCaptions(caps => {
      const cap = caps[ci]; if (!cap || cap.words.length < 2 || !cap.words[wi]) return;
      const w = cap.words[wi]; const wlen = Math.max(0.1, w.end - w.start);
      const others = cap.words.filter((_, k) => k !== wi);
      if (toStart) { const f = Math.min(...others.map(x => x.start)); w.start = Math.max(0, f - wlen); w.end = w.start + wlen; }
      else { const l = Math.max(...others.map(x => x.end)); w.start = l; w.end = l + wlen; }
    });
    setSelWord({ ci, wi: toStart ? 0 : len - 1 });
  }, [mutateCaptions, captions]);
  // Woord één plek naar links/rechts: wissel tijdslot met de buur — uit app.js.
  const swapWordOrder = useCallback((ci, wi, dir) => {
    const len = captions[ci]?.words?.length ?? 0;
    const j = wi + dir;
    if (j < 0 || j >= len) return;
    mutateCaptions(caps => {
      const cap = caps[ci]; if (!cap) return;
      cap.words.sort((a, b) => a.start - b.start);
      const a = cap.words[wi], b = cap.words[j];
      if (!a || !b) return;
      const as = a.start, ae = a.end; a.start = b.start; a.end = b.end; b.start = as; b.end = ae;
    });
    setSelWord({ ci, wi: j });   // selectie volgt het verplaatste woord
  }, [mutateCaptions, captions]);
  // Woord op een eigen regel zetten — uit app.js.
  const isolateWord = useCallback((ci, wi) => {
    mutateCaptions(caps => {
      const cap = caps[ci]; if (!cap || !cap.words[wi] || cap.words.length <= 1) return;
      const before = cap.words.slice(0, wi), word = cap.words[wi], after = cap.words.slice(wi + 1);
      const parts = [];
      if (before.length) parts.push({ words: before });
      parts.push({ words: [word] });
      if (after.length) parts.push({ words: after });
      caps.splice(ci, 1, ...parts);
    });
    setSelWord(null);
  }, [mutateCaptions]);
  // Rechtermuisknop-menu op een tijdlijn-woord.
  const [wordMenu, setWordMenu] = useState(null); // { x, y, ci, wi } | null
  const [posMenu, setPosMenu] = useState(null);   // { x, y, ci } | null
  const openWordMenu = useCallback((e, ci, wi) => {
    e.preventDefault(); e.stopPropagation();
    setSelWord({ ci, wi });
    setWordMenu({ x: e.clientX, y: e.clientY, ci, wi });
  }, []);
  const addWordAtTime = useCallback((t) => {
    const text = (window.prompt(`Nieuw woord op ${formatClock(t)}:`, '') || '').trim();
    if (!text) return;
    mutateCaptions(caps => {
      const start = Math.max(0, t); const end = start + 0.4;
      // voeg toe aan de caption die deze tijd bevat, anders een nieuwe caption.
      const host = caps.find(c => start >= c.start && start <= c.end);
      if (host) host.words.push({ text, start, end });
      else caps.push({ words: [{ text, start, end }], start, end, text });
    });
  }, [mutateCaptions]);

  // Voeg een woord toe aan het einde van een bestaande regel (Tekst-tab "+ woord").
  const addWordToCaption = useCallback((ci) => {
    const text = (window.prompt('Nieuw woord toevoegen:', '') || '').trim();
    if (!text) return;
    mutateCaptions(caps => {
      const cap = caps[ci]; if (!cap || !cap.words.length) return;
      const last = cap.words[cap.words.length - 1];
      const start = last.end; const end = start + 0.4;
      cap.words.push({ text, start, end });
    });
  }, [mutateCaptions]);

  // Pas de tekst van één woord aan (leeg = verwijderen). Tekst-tab.
  const editWordText = useCallback((ci, wi) => {
    setSelWord({ ci, wi });
    const cur = captions[ci]?.words?.[wi]?.text || '';
    const next = window.prompt('Woord aanpassen (leeg = verwijderen):', cur);
    if (next == null) return;
    const t = next.trim();
    mutateCaptions(caps => {
      const w = caps[ci]?.words?.[wi]; if (!w) return;
      if (!t) { caps[ci].words.splice(wi, 1); return; }
      w.text = t;
    });
  }, [captions, mutateCaptions]);

  // Slepen/timing van een woord-blok (mode: 'move' | 'l' | 'r').
  // ── Per-regel positie: sleep de actieve ondertitel vrij in de preview ─────────
  // Zet caption.posX/posY (fracties 0..1, zelfde eenheid als karaoke.js) als
  // override op de stijl-positie — bv. omdat de regel anders voor een gezicht zit.
  const startCaptionDrag = useCallback((event, ci) => {
    // Rechtsklik én Mac ctrl+klik (button 0 + ctrlKey) openen het positiemenu;
    // die mogen geen sleep/afspelen starten (de mouseup zou anders play/pauze geven).
    if (event.button !== 0 || event.ctrlKey) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;
    const cap = captions[ci];
    const startPos = {
      x: cap?.posX != null ? cap.posX : (style.posX ?? 50) / 100,
      y: cap?.posY != null ? cap.posY : (style.posY ?? 82) / 100,
    };
    const sx0 = event.clientX, sy0 = event.clientY;
    let moved = false;
    const onMove = (e) => {
      if (!moved && Math.abs(e.clientX - sx0) + Math.abs(e.clientY - sy0) > 4) moved = true;
      if (!moved) return;
      const dx = (e.clientX - sx0) / rect.width;
      const dy = (e.clientY - sy0) / rect.height;
      mutateCaptions(caps => {
        const c = caps[ci]; if (!c) return;
        c.posX = Math.min(0.98, Math.max(0.02, startPos.x + dx));
        c.posY = Math.min(0.97, Math.max(0.03, startPos.y + dy));
        delete c.posOvergenomen; // zelf gesleept = echt anker
      });
    };
    const onUp = (e) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Geen beweging = gewone klik → speel/pauzeer zoals klikken op de video.
      // Niet bij een (ctrl-)klik die het contextmenu opent.
      if (!moved && !(e && (e.ctrlKey || e.button !== 0))) togglePlayback();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [captions, style.posX, style.posY, mutateCaptions, togglePlayback]);
  const resetCaptionPos = useCallback((ci) => {
    mutateCaptions(caps => { const c = caps[ci]; if (c) { delete c.posX; delete c.posY; delete c.posOvergenomen; } });
  }, [mutateCaptions]);
  // Doorzetten: zie captionPos.js (ankers vs. overgenomen posities).
  // Zo springen opeenvolgende zinnen niet heen en weer.
  const applyCaptionPos = useCallback((ci, scope) => {
    const src = captions[ci];
    if (!hasOwnCaptionPos(src)) return;
    const targets = captionPosTargets(captions, ci, scope);
    if (!targets.length) { showNotice('Geen regels om de positie naar door te zetten', 'info'); return; }
    mutateCaptions(caps => { targets.forEach(i => { if (caps[i]) setCapPos(caps[i], src); }); });
    showNotice(`Positie doorgezet naar ${targets.length} ${targets.length === 1 ? 'regel' : 'regels'}`, 'success');
  }, [captions, mutateCaptions, showNotice]);
  const copyPrevCaptionPos = useCallback((ci) => {
    let pi = ci - 1;
    while (pi >= 0 && !hasOwnCaptionPos(captions[pi])) pi--;
    if (pi < 0) { showNotice('Geen eerdere regel met een eigen positie', 'info'); return; }
    const src = captions[pi];
    mutateCaptions(caps => { if (caps[ci]) setCapPos(caps[ci], src); });
  }, [captions, mutateCaptions, showNotice]);
  // Maak van deze regel-positie de standaard; regels met exact dezelfde positie
  // verliezen hun (nu overbodige) override.
  const makeCaptionPosDefault = useCallback((ci) => {
    const src = captions[ci];
    if (!hasOwnCaptionPos(src)) return;
    const x = src.posX != null ? src.posX : (style.posX ?? 50) / 100;
    const y = src.posY != null ? src.posY : (style.posY ?? 82) / 100;
    const round = (v) => Math.round(v * 1000) / 10;
    setStyle(prev => ({ ...prev, posX: round(x), posY: round(y) }));
    mutateCaptions(caps => {
      caps.forEach(c => {
        if ((c.posX ?? x) === x && (c.posY ?? y) === y) { delete c.posX; delete c.posY; delete c.posOvergenomen; }
      });
    });
    showNotice('Standaardpositie aangepast', 'success');
  }, [captions, style.posX, style.posY, mutateCaptions, showNotice]);

  const startWordDrag = useCallback((event, ci, wi, mode) => {
    // Rechtsklik (button 2) én Mac ctrl+klik (button 0 + ctrlKey) niet kapen →
    // dan kan het contextmenu openen i.p.v. een sleep te starten.
    if (event.button !== 0 || event.ctrlKey) return;
    event.stopPropagation();
    setSelWord({ ci, wi });
    const track = tlTrackRef.current;
    const dur = videoRef.current?.duration || duration || 0;
    if (!track || !dur) return;
    const pxWidth = track.getBoundingClientRect().width || 1;
    const startX = event.clientX;
    let orig = null;
    setCaptions(prev => { orig = { ...prev[ci]?.words?.[wi] }; return prev; });
    const onMove = (e) => {
      const dt = ((e.clientX - startX) / pxWidth) * dur;
      mutateCaptions(caps => {
        const w = caps[ci]?.words?.[wi]; if (!w || !orig) return;
        if (mode === 'move') { const len = orig.end - orig.start; w.start = Math.max(0, orig.start + dt); w.end = w.start + len; }
        else if (mode === 'l') { w.start = Math.min(orig.end - 0.05, Math.max(0, orig.start + dt)); w.end = orig.end; }
        else if (mode === 'r') { w.end = Math.max(orig.start + 0.05, orig.end + dt); w.start = orig.start; }
        if (videoRef.current) videoRef.current.currentTime = w.start;
      });
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [duration, mutateCaptions]);

  // ── Sneltoetsen (na alle helpers gedefinieerd i.v.m. dependency-volgorde) ──────
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (typing) return;
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      // Shift+Z (zonder cmd/ctrl) = hele tijdlijn passend tonen (uitzoomen naar fit).
      if (!meta && e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        setTlZoom(1);
        if (tlScrollRef.current) tlScrollRef.current.scrollLeft = 0;
        return;
      }
      // Bewerkingen op het geselecteerde tijdlijn-woord.
      if (selWord) {
        const { ci, wi } = selWord;
        if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); deleteWord(ci, wi); return; }
        if (e.key === 'e' || e.key === 'E') { e.preventDefault(); toggleEmph(ci, wi); return; }
        if (e.key === 'i' || e.key === 'I') { e.preventDefault(); toggleItalic(ci, wi); return; }
        if (e.key === 'u' || e.key === 'U') { e.preventDefault(); toggleUnderline(ci, wi); return; }
        if (e.key === 's' || e.key === 'S') { e.preventDefault(); splitAtWord(ci, wi); return; }
        if (e.key === 'Enter') { e.preventDefault(); toggleBreak(ci, wi); return; }
        if (e.key === 'ArrowLeft') { e.preventDefault(); nudgeWord(ci, wi, e.shiftKey ? -0.2 : -0.05, e.altKey); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); nudgeWord(ci, wi, e.shiftKey ? 0.2 : 0.05, e.altKey); return; }
      }
      const v = videoRef.current;
      if (e.key === ' ') { e.preventDefault(); togglePlayback(); return; }   // altijd 1×
      if (!v) return;
      if (e.key === 'k' || e.key === 'K') { e.preventDefault(); setShuttle(0); }   // pauze
      else if (e.key === 'l' || e.key === 'L') {
        // Vooruit; herhaald = sneller (1×→2×→4×→8×). Vanuit pauze/achteruit: 1×.
        e.preventDefault();
        const reversing = shuttleRef.current.rate < 0;
        const next = (v.paused || reversing) ? 1 : Math.min(8, (playRateRef.current || 1) * 2);
        setShuttle(next);
      } else if (e.key === 'j' || e.key === 'J') {
        // Achteruit; herhaald = sneller. Vanuit vooruit/pauze: 1× achteruit.
        e.preventDefault();
        const revRate = shuttleRef.current.rate < 0 ? Math.min(8, Math.abs(shuttleRef.current.rate) * 2) : 1;
        setShuttle(-revRate);
      } else if (e.key === 'Home') { e.preventDefault(); setShuttle(0); seekTo(0); }
      else if (e.key === 'End') { e.preventDefault(); setShuttle(0); seekTo(videoRef.current?.duration || duration); }
      else if (e.key === 'i' || e.key === 'I') { e.preventDefault(); setInPoint(v.currentTime || 0); }
      else if (e.key === 'o' || e.key === 'O') { e.preventDefault(); setOutPoint(v.currentTime || 0); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTl(1.3); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomTl(1 / 1.3); }
      else if (e.key === 'h' || e.key === 'H') { e.preventDefault(); setHandTool(t => !t); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlayback, setShuttle, undo, redo, seekTo, duration, zoomTl, selWord, deleteWord, toggleEmph, toggleItalic, toggleUnderline, splitAtWord, toggleBreak, nudgeWord]);

  const applyTemplate = useCallback((template) => {
    setStyle(previous => ({
      ...previous,
      template: template.id,
      activeColor: template.activeColor,
      backgroundColor: template.backgroundColor,
    }));
  }, []);

  // Pas een originele karaoke-template (RITMO.STYLES) toe: zet de id + neem de
  // relevante defaults over (rest rendert de gedeelde renderer zelf).
  const applyKaraokeStyle = useCallback((s) => {
    const d = s.defaults || {};
    setStyle(prev => ({
      ...prev,
      template: s.id,
      activeColor: d.activeColor || prev.activeColor,
      textColor: (d.baseColor && d.baseColor !== 'transparent') ? d.baseColor : prev.textColor,
      stroke: d.strokeWidth != null ? d.strokeWidth : prev.stroke,
      strokeColor: d.strokeColor || prev.strokeColor,
      bold: d.fontWeight != null ? d.fontWeight >= 800 : prev.bold,
      uppercase: d.uppercase != null ? d.uppercase : prev.uppercase,
      boxOn: d.boxOn != null ? d.boxOn : prev.boxOn,
    }));
  }, []);

  // Stijl-toggle: 'standaard' = rustige regels (geen woord-highlight), 'ritme' = karaoke.
  // Bij 'standaard' kiezen we een rustige template als de huidige een pop/box-stijl is.
  const setMode = useCallback((mode) => {
    if (!mode) return;
    setStyle(previous => {
      const next = { ...previous, mode };
      if (mode === 'standaard') {
        const calmIds = TEMPLATES.filter(t => t.calm).map(t => t.id);
        if (!calmIds.includes(previous.template)) {
          const clean = TEMPLATES.find(t => t.id === 'clean');
          next.template = clean.id;
          next.activeColor = clean.activeColor;
          next.backgroundColor = clean.backgroundColor;
        }
        if (previous.position === 'center') next.position = 'lower-third';
      }
      return next;
    });
  }, []);

  const applyAspectPreset = useCallback((key) => {
    const preset = ASPECT_PRESETS[key];
    if (!preset) return;
    setStyle(previous => {
      const next = { ...previous, aspectRatio: key };
      if (preset.fontSize) next.fontSize = preset.fontSize;
      if (preset.position) next.position = preset.position;
      return next;
    });
    if (preset.wordsPerLine) {
      const wpl = preset.wordsPerLine;
      setStyle(prev => ({ ...prev, wordsPerLine: wpl }));
      setCaptions(prev => buildCaptions(prev.flatMap(c => c.words || []), wpl));
    }
  }, []);

  const applyBrandPreset = useCallback((preset) => {
    setStyle(previous => ({
      ...previous,
      textColor: preset.textColor,
      activeColor: preset.activeColor,
      backgroundColor: preset.backgroundColor,
    }));
  }, []);

  const saveProject = useCallback(async () => {
    if (!uploadName) {
      showNotice('Kies eerst een video voordat je opslaat', 'warning');
      return;
    }

    setSaving(true);
    try {
      const doc = { ...projectFromState({ projectId, projectName, uploadName, videoMeta, captions, style }),
        ...(klantProject.trim() ? { klant_project: klantProject.trim() } : {}) };
      const saved = await ritmoFetch('/project/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: projectId, name: projectName, doc }),
      }).then(res => res.json());
      setProjectId(saved.id);
      showNotice('Project opgeslagen', 'success');
      loadProjects();
    } catch (error) {
      showNotice(error.message, 'error');
    } finally {
      setSaving(false);
    }
  }, [captions, klantProject, loadProjects, projectId, projectName, ritmoFetch, showNotice, style, uploadName, videoMeta]);

  const loadProject = useCallback(async (id) => {
    try {
      const record = await ritmoFetch(`/project/${id}`).then(res => res.json());
      const doc = record.doc || {};
      setProjectId(record.id);
      setKlantProject(doc.klant_project || '');
      setProjectName(doc.name || record.name || 'Naamloos project');
      setBackendUploadName(doc.uploadName || '');
      setBackendUploadToken('');
      selectedFileRef.current = null;
      setUploadStatus(doc.uploadName ? 'ready' : 'idle');
      setUploadError('');
      setVideoMeta(doc.video || null);
      setCaptions(doc.captions || []);
      setStyle({ ...DEFAULT_STYLE, ...(doc.style || {}) });
      setMediaUrl(mediaUrlForUpload(doc.uploadName));
      setPreviewUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return '';
      });
      setCurrentTime(0);
      setJob(null);
      setJobId(null);
      showNotice('Project geopend', 'success');
    } catch (error) {
      showNotice(error.message, 'error');
    }
  }, [ritmoFetch, setBackendUploadName, setBackendUploadToken, showNotice]);

  const exportSubtitles = useCallback(async (format) => {
    if (!captions.length) {
      showNotice('Er zijn nog geen captions om te exporteren', 'warning');
      return;
    }

    try {
      const project = projectFromState({ projectId, projectName, uploadName, videoMeta, captions, style });
      const data = await ritmoFetch('/export/subtitles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project, name: projectName, format, lang: 'nl', ellipsisStyle: subEllipsis, lineBreak: true }),
      }).then(res => res.json());
      if (data.download) window.open(data.download, '_blank', 'noopener,noreferrer');
      showNotice(`${format.toUpperCase()} export klaar`, 'success');
    } catch (error) {
      showNotice(error.message, 'error');
    }
  }, [captions, projectId, projectName, ritmoFetch, showNotice, style, uploadName, videoMeta, subEllipsis]);

  // FCPXML-export (Final Cut) — backend heeft hiervoor al /export/fcpxml.
  const exportFcpxml = useCallback(async () => {
    if (!captions.length) {
      showNotice('Er zijn nog geen captions om te exporteren', 'warning');
      return;
    }
    try {
      const project = projectFromState({ projectId, projectName, uploadName, videoMeta, captions, style });
      const data = await ritmoFetch('/export/fcpxml', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project, name: projectName }),
      }).then(res => res.json());
      if (data.download) window.open(data.download, '_blank', 'noopener,noreferrer');
      showNotice('FCPXML-export klaar', 'success');
    } catch (error) {
      showNotice(error.message, 'error');
    }
  }, [captions, projectId, projectName, ritmoFetch, showNotice, style, uploadName, videoMeta]);

  // Video met ingebrande ondertitels renderen op de render-server (MP4 of ProRes).
  const exportVideo = useCallback(async (type = 'mp4') => {
    const sourceName = uploadNameRef.current || uploadName;
    if (!captions.length) { showNotice('Er zijn nog geen captions om te renderen', 'warning'); return; }
    if (!sourceName) { showNotice('Geen bronvideo gevonden — kies eerst een video', 'warning'); return; }
    // Waarschuw als het gekozen font niet beschikbaar lijkt — de render valt dan terug
    // op een systeemfont (item 12, render-kant).
    if (!fontAvailable(style.fontFamily || 'Inter') &&
        !(await bevestig({ titel: 'Toch renderen?', tekst: `Lettertype "${style.fontFamily}" lijkt niet beschikbaar — de render valt mogelijk terug op een ander font.`, knop: 'Renderen' }))) {
      return;
    }
    try {
      const project = projectFromState({ projectId, projectName, uploadName: sourceName, videoMeta, captions, style });
      // Parity: stuur de EXACT dezelfde karaoke-projectvorm mee die de live-preview
      // gebruikt (styleId + font + álle overrides). De render-server rendert hiermee
      // 1:1 wat je in de editor ziet — i.p.v. een aparte, incomplete vertaling.
      project.karaoke = buildPreviewProject();
      // Fonts die de headless renderer moet laden zodat het lettertype matcht
      // (anders valt Chromium terug op een systeemfont → serif/sans-mismatch).
      const googleHref = `https://fonts.googleapis.com/css2?${FONT_OPTIONS.filter(f => f.google).map(f => `family=${f.google}`).join('&')}&display=swap`;
      project.fonts = {
        google: googleHref, adobeKit: (adobeKit || '').trim() || undefined, family: style.fontFamily || 'Inter',
        // Geüploade fonts: de render-worker installeert ze en bedt ze in de renderer in.
        custom: uploadedFonts.map(f => ({ family: f.name, file: f.filename })),
      };
      setRenderJob({ status: 'running', progress: 0, message: 'Render-job wordt gestart...' });
      // Export-opties als job-velden — de render-server leest deze uit job.options.
      const range = (exportRangeOnly && inPoint != null && outPoint != null && outPoint > inPoint)
        ? { start: inPoint, end: outPoint } : undefined;
      // prores én prores-alpha gaan naar het prores-endpoint (alpha = transparante
      // achtergrond, ProRes 4444 — voor op een edit). Alleen 'mp4' → mp4-endpoint.
      const endpoint = (type === 'prores' || type === 'prores-alpha') ? 'prores' : 'mp4';
      const data = await ritmoFetch(`/export/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project, name: sourceName, alpha: type === 'prores-alpha',
          fast: exportFast || undefined,
          scale: exportScale ? Number(exportScale) : undefined,
          range,
          outputDir: (outputDir || '').trim() || undefined,
          ...(endpoint === 'mp4' ? { mp4Preset } : {}),
        }),
      }).then(res => res.json());
      if (!data.job) throw new Error(data.detail || 'Geen render-job ontvangen');
      setRenderJobId(data.job);
      showNotice('Render-job gestart op de render-server', 'info');
    } catch (error) {
      setRenderJob({ status: 'error', error: error.message });
      showNotice(error.message, 'error');
    }
  }, [captions, projectId, projectName, ritmoFetch, showNotice, style, uploadName, videoMeta, exportFast, exportScale, exportRangeOnly, mp4Preset, outputDir, fontAvailable, inPoint, outPoint, buildPreviewProject, adobeKit]);

  // Poll de render-job tot done/error en toon voortgang + downloadlink.
  useEffect(() => {
    if (!renderJobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await ritmoPublicFetch(`/jobs/${renderJobId}`).then(res => res.json());
        if (cancelled) return;
        setRenderJob(data);
        if (data.status === 'done') {
          setRenderJobId(null);
          showNotice('Video-render klaar!', 'success');
          return;
        }
        if (data.status === 'error') {
          setRenderJobId(null);
          showNotice(data.error || data.message || 'Render mislukt', 'error');
          return;
        }
        renderPollRef.current = window.setTimeout(poll, 2500);
      } catch (error) {
        if (!cancelled) { setRenderJob({ status: 'error', error: error.message }); setRenderJobId(null); }
      }
    };
    poll();
    return () => { cancelled = true; if (renderPollRef.current) window.clearTimeout(renderPollRef.current); };
  }, [renderJobId, ritmoPublicFetch, showNotice]);

  // Pas de tekst van een caption aan. Herverdeelt de nieuwe woorden gelijkmatig over de
  // bestaande tijdspanne zodat export (SRT/VTT/FCPXML) én de karaoke-weergave kloppen.
  const updateCaptionText = useCallback((index, newText) => {
    setCaptions(prev => prev.map((caption, i) => {
      if (i !== index) return caption;
      const tokens = String(newText).trim().split(/\s+/).filter(Boolean);
      const span = (caption.end - caption.start) || 0;
      const per = tokens.length ? span / tokens.length : 0;
      const words = tokens.map((tok, k) => ({
        text: tok,
        start: caption.start + per * k,
        end: caption.start + per * (k + 1),
      }));
      return { ...caption, text: tokens.join(' '), words: words.length ? words : caption.words };
    }));
  }, []);

  const beginEditCaption = useCallback((index, text) => {
    setEditingCaption(index);
    setEditDraft(text || '');
  }, []);

  const commitEditCaption = useCallback(() => {
    if (editingCaption !== null) updateCaptionText(editingCaption, editDraft);
    setEditingCaption(null);
  }, [editingCaption, editDraft, updateCaptionText]);

  // Spellingcontrole: stuur alle woorden naar de backend (OpenAI, bewaakt) en pas de
  // gecorrigeerde woorden 1:1 terug toe — de timing blijft intact (zelfde aantal woorden).
  const [spellBusy, setSpellBusy] = useState(false);
  const runSpellCheck = useCallback(async () => {
    const flatWords = captions.flatMap(c => (c.words || []).map(w => w.text));
    if (!flatWords.length) { showNotice('Nog geen woorden om te controleren', 'warning'); return; }
    setSpellBusy(true);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/ritmo/public/grammar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ words: flatWords, project_nummer: klantProject.trim() || null, ritmo_project_id: projectId || null, bron_naam: uploadName || null }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Spellingcheck mislukt');
      const corr = Array.isArray(data.corrections) ? data.corrections : [];
      if (!corr.length) { showNotice('Geen spelfouten gevonden 👍', 'success'); return; }
      const byIndex = new Map(corr.map(c => [c.i, c.to]));
      let k = 0;
      setCaptions(prev => prev.map(c => ({
        ...c,
        words: (c.words || []).map(w => {
          const to = byIndex.get(k); k += 1;
          return (to != null) ? { ...w, text: to } : w;
        }),
      })).map(reflowCap));
      showNotice(`${corr.length} spelling-correctie(s) toegepast`, 'success');
    } catch (e) {
      showNotice(e.message || 'Spellingcheck mislukt', 'error');
    } finally {
      setSpellBusy(false);
    }
  }, [uploadName, klantProject, projectId, captions, showNotice]);

  const workerLabel = workerStatus?.online
    ? 'Render-server online'
    : workerStatus?.configured
      ? 'Render-server offline'
      : 'Render-server niet ingesteld';

  const workerColor = workerStatus?.online ? 'success' : workerStatus?.configured ? 'warning' : 'default';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        bgcolor: '#0c0f16',
        color: '#f8fafc',
        display: 'grid',
        gridTemplateRows: `58px 1fr ${timelineHeight}px`,
        overflow: 'hidden',
      }}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
          bgcolor: '#11151f',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
          <Typography variant="h6" fontWeight={900} letterSpacing={0.2} sx={{ whiteSpace: 'nowrap' }}>
            Ondertitel Studio
          </Typography>
        </Stack>

        {/* Stijl-toggle: rustige regels of karaoke — altijd zichtbaar, op elk moment om te zetten. */}
        <ToggleButtonGroup
          value={style.mode}
          exclusive
          size="small"
          onChange={(_e, v) => setMode(v)}
          sx={{
            bgcolor: '#0c0f16',
            borderRadius: 1.5,
            '& .MuiToggleButton-root': {
              color: '#94a3b8',
              border: '1px solid rgba(148, 163, 184, 0.22)',
              px: 1.5,
              py: 0.4,
              fontWeight: 700,
              textTransform: 'none',
              '&.Mui-selected': { bgcolor: '#67e8c9', color: '#042f2e', '&:hover': { bgcolor: '#5eead4' } },
            },
          }}
        >
          <ToggleButton value="standaard">Standaard</ToggleButton>
          <ToggleButton value="ritme">Ritmo</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          value={projectName}
          onChange={(event) => setProjectName(event.target.value)}
          variant="standard"
          size="small"
          sx={{
            minWidth: 260,
            maxWidth: 460,
            flex: 1,
            '& input': { color: '#e5e7eb', fontWeight: 700 },
            '& .MuiInput-underline:before': { borderBottomColor: 'transparent' },
            '& .MuiInput-underline:hover:before': { borderBottomColor: 'rgba(148, 163, 184, 0.35)' },
          }}
        />

        <Chip
          size="small"
          color={workerColor}
          variant={workerStatus?.online ? 'filled' : 'outlined'}
          label={workerLabel}
          sx={{ color: workerStatus?.online ? '#052e16' : '#cbd5e1', borderColor: 'rgba(148, 163, 184, 0.35)' }}
        />

        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ color: '#e5e7eb', borderColor: 'rgba(148, 163, 184, 0.35)' }}
        >
          Andere video
        </Button>
        <Button
          variant="outlined"
          startIcon={<FolderOpenIcon />}
          onClick={() => {
            setRightTab('projects');
            loadProjects();
          }}
          sx={{ color: '#e5e7eb', borderColor: 'rgba(148, 163, 184, 0.35)' }}
        >
          Open
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
          onClick={saveProject}
          disabled={saving}
          sx={{ bgcolor: '#f8fafc', color: '#111827', '&:hover': { bgcolor: '#e5e7eb' } }}
        >
          Opslaan
        </Button>
        <Tooltip title="Handboek &amp; sneltoetsen" arrow>
          <IconButton onClick={() => setShowHelp(true)} sx={{ color: '#e5e7eb', border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 1 }}>
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>

        <input ref={fileInputRef} type="file" accept="video/*" hidden onChange={handleInputChange} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 400px' },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: 'grid',
            gridTemplateRows: '1fr 72px',
            borderRight: { lg: '1px solid rgba(148, 163, 184, 0.18)' },
          }}
        >
          <Box
            onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            sx={{
              position: 'relative',
              minHeight: 0,
              display: 'grid',
              placeItems: 'center',
              bgcolor: dragActive ? '#131a27' : '#0c0f16',
              transition: 'background-color 140ms ease',
              overflow: 'hidden',
            }}
          >
            {hasVideo && (
              <>
                {/* Veilige zone (links) + viewer-zoom (rechts). Native <select> — werkt
                    altijd, ook over de video (MUI Select haperde in deze overlay). */}
                <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 7, display: 'flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'rgba(18,20,26,.82)', border: '1px solid rgba(148,163,184,.3)', borderRadius: 1.5, px: 1, py: 0.5, backdropFilter: 'blur(6px)' }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, letterSpacing: .4, textTransform: 'uppercase', fontSize: 10 }}>Veilige zone</Typography>
                  <Box component="select" value={safeZone} onChange={(e) => setSafeZone(e.target.value)}
                    sx={{ color: '#e5e7eb', fontSize: 12, bgcolor: 'transparent', border: 'none', outline: 'none', cursor: 'pointer', '& option': { color: '#111' } }}>
                    <option value="none">Geen</option>
                    {Object.keys(SAFE_ZONES).map((k) => <option key={k} value={k}>{SAFE_LABELS[k]}</option>)}
                  </Box>
                </Box>
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 7, display: 'flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'rgba(18,20,26,.82)', border: '1px solid rgba(148,163,184,.3)', borderRadius: 1.5, px: 1, py: 0.5, backdropFilter: 'blur(6px)' }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, letterSpacing: .4, textTransform: 'uppercase', fontSize: 10 }}>Zoom</Typography>
                  <Box component="select" value={viewZoom} onChange={(e) => setViewZoom(e.target.value)}
                    sx={{ color: '#e5e7eb', fontSize: 12, bgcolor: 'transparent', border: 'none', outline: 'none', cursor: 'pointer', '& option': { color: '#111' } }}>
                    <option value="fit">Passend</option>
                    <option value="0.5">50%</option>
                    <option value="1.5">150%</option>
                    <option value="2">200%</option>
                    <option value="native">1:1 (native pixels)</option>
                  </Box>
                </Box>
              </>
            )}
            {hasVideo ? (
              <Box
                ref={previewScrollRef}
                onMouseDown={(handTool && zoomScale > 1) ? startPreviewPan : undefined}
                sx={{ position: 'relative', width: '100%', height: '100%', display: 'grid', placeItems: 'center', p: 3,
                  overflow: zoomScale > 1 ? 'auto' : 'hidden',
                  cursor: (handTool && zoomScale > 1) ? 'grab' : 'default' }}>
                <Box
                  ref={previewBoxRef}
                  sx={{
                    position: 'relative',
                    // Expliciete, in JS berekende contain-fit — betrouwbaar voor staand én liggend.
                    width: fitDims ? `${fitDims.w}px` : 'auto',
                    height: fitDims ? `${fitDims.h}px` : 'auto',
                    // Fallback zolang de afmetingen nog niet gemeten zijn.
                    maxWidth: '100%',
                    maxHeight: '100%',
                    aspectRatio: fitDims ? undefined : (videoMeta ? `${videoMeta.width} / ${videoMeta.height}` : '9 / 16'),
                    display: 'grid',
                    placeItems: 'center',
                    transform: zoomScale !== 1 ? `scale(${zoomScale})` : 'none',
                    transformOrigin: 'center',
                    flexShrink: 0,
                  }}
                >
                  {SAFE_ZONES[safeZone] && (() => {
                    const bucket = aspectBucket(videoMeta);
                    const z = SAFE_ZONES[safeZone][bucket] || SAFE_ZONES[safeZone]['9x16'];
                    return (
                    <Box sx={{ position: 'absolute', zIndex: 4, pointerEvents: 'none',
                      top: `${z.top}%`, bottom: `${z.bottom}%`, left: `${z.left}%`, right: `${z.right}%`,
                      border: '2px dashed rgba(255,255,255,.78)', borderRadius: 1 }}>
                      <Typography sx={{ position: 'absolute', top: 4, left: 6, fontSize: 10, color: '#fff',
                        bgcolor: 'rgba(0,0,0,.55)', px: 0.75, borderRadius: 0.5 }}>
                        Veilige zone · {SAFE_LABELS[safeZone]} ({BUCKET_LABEL[bucket]})
                      </Typography>
                    </Box>
                    );
                  })()}
                  <Box
                    component="video"
                    ref={videoRef}
                    src={previewUrl || mediaUrl}
                    playsInline
                    preload="metadata"
                    onClick={togglePlayback}
                    onTimeUpdate={handleVideoTime}
                    onLoadedMetadata={handleVideoTime}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 1,
                      bgcolor: '#000',
                      boxShadow: '0 22px 80px rgba(0, 0, 0, 0.42)',
                      cursor: 'pointer',
                    }}
                  />

                  {/* Parity-preview via de gedeelde renderer (= export). Valt terug op
                      de React-preview als karaoke.js niet geladen is. */}
                  {ritmoReady && (
                    <Box ref={stageRef} sx={{ position: 'absolute', top: 0, left: 0, transformOrigin: 'top left', pointerEvents: 'none', zIndex: 3 }} />
                  )}
                  {/* Sleep-laag voor de actieve ondertitel: verplaats déze regel vrij in
                      beeld (per-regel override, bv. weg van een gezicht). Klik zonder
                      slepen = play/pauze; dubbelklik = terug naar de standaard-positie. */}
                  {hasVideo && activeCaption && !handTool && (() => {
                    const ci = captions.indexOf(activeCaption);
                    if (ci < 0) return null;
                    const capY = activeCaption.posY != null ? activeCaption.posY * 100 : (style.posY ?? 82);
                    const hasOwnPos = activeCaption.posX != null || activeCaption.posY != null;
                    return (
                      <Box
                        onMouseDown={(e) => startCaptionDrag(e, ci)}
                        onDoubleClick={() => resetCaptionPos(ci)}
                        onContextMenu={(e) => { e.preventDefault(); setPosMenu({ x: e.clientX, y: e.clientY, ci }); }}
                        title="Sleep om alleen deze ondertitel-regel te verplaatsen · dubbelklik = standaard-positie · rechterklik = positie doorzetten"
                        sx={{ position: 'absolute', zIndex: 5, left: 0, right: 0, top: `${capY}%`,
                          height: '15%', transform: 'translateY(-50%)', cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                          '&:hover': { outline: '1px dashed rgba(103,232,201,.55)', outlineOffset: -2 } }}
                      >
                        {hasOwnPos && (
                          <Typography sx={{ position: 'absolute', top: 2, right: 6, fontSize: 10, color: '#67e8c9',
                            bgcolor: 'rgba(2,6,23,.65)', px: 0.75, borderRadius: 0.5, pointerEvents: 'none' }}>
                            {activeCaption.posOvergenomen ? '📍 overgenomen positie' : '📍 eigen positie'}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                  {!ritmoReady && activeCaption && (() => {
                    const fs = style.fontSize;
                    const boxBg = style.boxOn ? hexToRgba(style.boxColor, (style.boxOpacity ?? 60) / 100) : 'transparent';
                    const shadows = [];
                    if (style.shadow) shadows.push(`0 ${Math.round((style.shadow / 100) * 4)}px ${Math.round((style.shadow / 100) * 18)}px ${hexToRgba(style.shadowColor, Math.min(0.9, style.shadow / 100))}`);
                    if (style.glow) { const g = hexToRgba(style.glowColor, Math.min(1, style.glow / 100)); shadows.push(`0 0 ${Math.round((style.glow / 100) * 22)}px ${g}`, `0 0 ${Math.round((style.glow / 100) * 40)}px ${g}`); }
                    const textShadow = shadows.join(', ') || 'none';
                    const strokeCss = style.stroke ? { WebkitTextStroke: `${style.stroke}px ${style.strokeColor}`, paintOrder: 'stroke fill' } : {};
                    return (
                    <Box
                      sx={{
                        position: 'absolute', left: '6%', right: '6%',
                        // Vrije verticale positie (posY) heeft voorrang op de preset.
                        ...(style.posY != null
                          ? { top: `${style.posY}%`, bottom: 'auto', transform: 'translateY(-50%)' }
                          : (POSITION_STYLE[style.position] || POSITION_STYLE.center)),
                        display: 'flex',
                        justifyContent: style.posX < 40 ? 'flex-start' : style.posX > 60 ? 'flex-end' : 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex', flexWrap: 'wrap', alignItems: 'baseline',
                          justifyContent: 'center',
                          columnGap: `${((style.wordGap ?? 28) / 100) * fs * 0.08}vw`,
                          rowGap: `${((style.lineGap ?? 15) / 100) * fs * 0.08}vw`,
                          px: style.boxOn ? `${(style.boxPad ?? 20) / 100 * 1.6}em` : 0,
                          py: style.boxOn ? `${(style.boxPad ?? 20) / 100 * 0.9}em` : 0,
                          borderRadius: `${(style.boxRadius ?? 18) / 100 * 1.4}em`,
                          bgcolor: boxBg,
                          fontFamily: style.fontFamily,
                          fontSize: `clamp(20px, ${fs / 14}vw, ${fs}px)`,
                          fontWeight: style.bold ? 900 : 800,
                          fontStyle: style.italic ? 'italic' : 'normal',
                          textDecoration: style.underline ? 'underline' : 'none',
                          lineHeight: 1.1,
                          textTransform: style.uppercase ? 'uppercase' : 'none',
                          ...strokeCss,
                        }}
                      >
                        {activeCaption.words.map((word, index) => {
                          const standaard = style.mode === 'standaard';
                          const active = currentTime >= word.start && currentTime <= word.end;
                          const past = currentTime > word.end;
                          const wordColor = word.emph ? style.emphColor
                            : standaard ? style.textColor
                            : active ? style.activeColor
                            : past ? style.pastColor
                            : style.textColor;
                          const popped = !standaard && !style.noMotion && active;
                          const scale = word.emph ? (style.emphScale ?? 122) / 100 : popped ? 1.06 : 1;
                          return (
                            <Box key={`${word.start}-${word.text}-${index}`} component="span"
                              sx={{
                                color: wordColor, textShadow,
                                transition: 'color 80ms ease, transform 90ms ease',
                                display: 'inline-block', transform: `scale(${scale})`,
                                fontWeight: word.emph ? 900 : undefined,
                                fontStyle: word.italic ? 'italic' : undefined,
                                textDecoration: word.underline ? 'underline' : undefined,
                              }}>
                              {word.text}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                    );
                  })()}
                </Box>
              </Box>
            ) : (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  width: 'min(620px, calc(100% - 48px))',
                  minHeight: 250,
                  border: '1px dashed rgba(148, 163, 184, 0.42)',
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: 'rgba(15, 23, 42, 0.45)',
                  p: 4,
                  '&:hover': { borderColor: '#67e8c9', bgcolor: 'rgba(20, 184, 166, 0.08)' },
                }}
              >
                <Stack spacing={1.5} alignItems="center">
                  <CloudUploadIcon sx={{ fontSize: 56, color: '#67e8c9' }} />
                  <Typography variant="h5" fontWeight={800}>Sleep een video hierheen</Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    of klik om te kiezen. De render en transcriptie lopen via onze backend/render-server flow.
                  </Typography>
                </Stack>
              </Box>
            )}

            {uploading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(2, 6, 23, 0.58)' }}>
                <Stack spacing={2} alignItems="center">
                  <CircularProgress sx={{ color: '#67e8c9' }} />
                  <Typography>Video uploaden...</Typography>
                </Stack>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 0.75,
              px: 2,
              borderTop: '1px solid rgba(148, 163, 184, 0.18)',
              bgcolor: '#11151f',
              // Controlebalk ligt altijd bovenop en is volledig klikbaar.
              position: 'relative',
              zIndex: 5,
            }}
          >
            {/* Volle-breedte scrubber: zelfde breedte/links-uitlijning als de TIJDLIJN
                eronder, zodat het rode uiteinde exact onder de tijdlijn-cursor valt. */}
            <Box
              onMouseDown={hasVideo && duration ? startTimelineScrub : undefined}
              sx={{
                width: '100%',
                py: 0.5,
                cursor: hasVideo && duration ? 'pointer' : 'default',
              }}
            >
              <LinearProgress
                variant="determinate"
                value={duration ? Math.min(100, (currentTime / duration) * 100) : 0}
                sx={{
                  height: 5,
                  borderRadius: 999,
                  bgcolor: 'rgba(148, 163, 184, 0.16)',
                  pointerEvents: 'none',
                  '& .MuiLinearProgress-bar': { bgcolor: '#ef4444' },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={togglePlayback}
              disabled={!hasVideo}
              sx={{ color: '#f8fafc', border: '1px solid rgba(148, 163, 184, 0.24)', borderRadius: 1 }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <Typography variant="body2" fontFamily="monospace" sx={{ color: '#cbd5e1', minWidth: 112 }}>
              {formatClock(currentTime)} / {formatClock(duration)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ width: 220, bgcolor: '#fff', borderRadius: 1, p: 0.5 }}>
              <EuProjectVeld waarde={klantProject} onChange={setKlantProject} compact />
            </Box>
            <Tooltip title={transcribeDisabledReason || 'Gaat het om een klant die alleen EU-verwerking toestaat (bv. Schneider)? Vul dan eerst het klantproject in.'} arrow>
              <span>
                <Button
                  variant="contained"
                  startIcon={transcribing ? <CircularProgress size={16} color="inherit" /> : <GraphicEqIcon />}
                  onClick={startTranscription}
                  disabled={!canStartTranscription}
                  sx={{
                    // 'background' (niet bgcolor): overschrijft de containedPrimary-gradient
                    // uit het thema (theme.js), anders blijft de knop grijs/slate.
                    background: '#67e8c9',
                    color: '#042f2e',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    '&:hover': { background: '#5eead4' },
                    '&.Mui-disabled': { background: 'rgba(103, 232, 201, 0.25)', color: 'rgba(4, 47, 46, 0.55)' },
                  }}
                >
                  {uploadStatus === 'error' && !uploadName ? 'Upload opnieuw & herken' : 'Spraak herkennen'}
                </Button>
              </span>
            </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, minHeight: 0, bgcolor: '#151a25', display: { xs: 'none', lg: 'grid' }, gridTemplateRows: '49px 1fr' }}>
          <Tabs
            value={rightTab}
            onChange={(_event, value) => setRightTab(value)}
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsToggle
            TabIndicatorProps={{ sx: { bgcolor: '#ef4444' } }}
            sx={{ minHeight: 49, borderBottom: '1px solid rgba(148, 163, 184, 0.18)', '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 } }}
          >
            <Tab value="templates" label="Templates" sx={{ color: '#8b97a8', fontWeight: 600, textTransform: 'none', minHeight: 49, minWidth: 'auto', px: 1.5, '&.Mui-selected': { color: '#ffffff', fontWeight: 800 } }} />
            <Tab value="opmaak" label="Opmaak" sx={{ color: '#8b97a8', fontWeight: 600, textTransform: 'none', minHeight: 49, minWidth: 'auto', px: 1.5, '&.Mui-selected': { color: '#ffffff', fontWeight: 800 } }} />
            <Tab value="text" label="Tekst" sx={{ color: '#8b97a8', fontWeight: 600, textTransform: 'none', minHeight: 49, minWidth: 'auto', px: 1.5, '&.Mui-selected': { color: '#ffffff', fontWeight: 800 } }} />
            <Tab value="export" label="Export" sx={{ color: '#8b97a8', fontWeight: 600, textTransform: 'none', minHeight: 49, minWidth: 'auto', px: 1.5, '&.Mui-selected': { color: '#ffffff', fontWeight: 800 } }} />
            <Tab value="projects" label="Projecten" sx={{ color: '#8b97a8', fontWeight: 600, textTransform: 'none', minHeight: 49, minWidth: 'auto', px: 1.5, '&.Mui-selected': { color: '#ffffff', fontWeight: 800 } }} />
          </Tabs>

          <Box sx={{ minHeight: 0, overflow: 'auto', p: 2 }}>
            {rightTab === 'templates' && (
              <Box>
                {/* Formaat — aspect-ratio-preset met zinnige defaults per doelformaat. */}
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>
                  FORMAAT
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, mb: 2 }}>
                  {Object.entries(ASPECT_PRESETS).map(([key, preset]) => {
                    const selected = style.aspectRatio === key;
                    return (
                      <Chip
                        key={key}
                        label={preset.label}
                        size="small"
                        onClick={() => applyAspectPreset(key)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 700,
                          color: selected ? '#042f2e' : '#cbd5e1',
                          bgcolor: selected ? '#67e8c9' : '#222a38',
                          '&:hover': { bgcolor: selected ? '#5eead4' : '#2b3445' },
                        }}
                      />
                    );
                  })}
                </Box>

                {/* Positie — lower-third (broadcast) / gecentreerd / bovenin. */}
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>
                  POSITIE
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, mb: 2 }}>
                  {POSITION_PRESETS.map(pos => {
                    const selected = style.position === pos.id;
                    return (
                      <Chip
                        key={pos.id}
                        label={pos.label}
                        size="small"
                        onClick={() => setStyle(prev => ({ ...prev, position: pos.id, posY: pos.id === 'top' ? 15 : pos.id === 'lower-third' ? 82 : 50 }))}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 700,
                          color: selected ? '#042f2e' : '#cbd5e1',
                          bgcolor: selected ? '#67e8c9' : '#222a38',
                          '&:hover': { bgcolor: selected ? '#5eead4' : '#2b3445' },
                        }}
                      />
                    );
                  })}
                </Box>

                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>
                  TEMPLATE
                </Typography>
                {(() => {
                  // Volledige stijl-lijst uit de render-engine (RITMO.STYLES, 30+ stijlen),
                  // met fallback op de ingebouwde set zolang de engine nog laadt.
                  const allTemplates = (ritmoReady && window.RITMO?.STYLES?.length)
                    ? window.RITMO.STYLES.map(s => ({
                        id: s.id, name: s.name, kind: s.category,
                        activeColor: s.defaults?.activeColor || '#67e8c9',
                        backgroundColor: s.defaults?.backgroundColor || 'transparent', _k: s,
                      }))
                    : TEMPLATES;
                  // Categorieën (zoals OG: Box / Karaoke / Neon / Bold / Reveal / …).
                  const cats = ['Alle', '★ Favoriet', ...Array.from(new Set(allTemplates.map(t => t.kind).filter(Boolean)))];
                  const filtered = allTemplates
                    .filter(t => tplCat === 'Alle' ? true : tplCat === '★ Favoriet' ? tplFavs.has(t.id) : t.kind === tplCat)
                    // Favorieten eerst (binnen de huidige filter).
                    .sort((a, b) => (tplFavs.has(b.id) ? 1 : 0) - (tplFavs.has(a.id) ? 1 : 0));
                  return (
                    <>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, mb: 1.5 }}>
                        {cats.map(cat => {
                          const sel = tplCat === cat;
                          return (
                            <Chip key={cat} label={cat} size="small" onClick={() => setTplCat(cat)}
                              sx={{ cursor: 'pointer', fontWeight: 700,
                                color: sel ? '#042f2e' : '#cbd5e1', bgcolor: sel ? '#67e8c9' : '#222a38',
                                '&:hover': { bgcolor: sel ? '#5eead4' : '#2b3445' } }} />
                          );
                        })}
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        {filtered.map(template => {
                          const selected = style.template === template.id;
                          const fav = tplFavs.has(template.id);
                          return (
                            <Box
                              key={template.id}
                              onClick={() => template._k ? applyKaraokeStyle(template._k) : applyTemplate(template)}
                              sx={{
                                position: 'relative',
                                border: '1px solid',
                                borderColor: selected ? '#ef4444' : 'rgba(148, 163, 184, 0.14)',
                                borderRadius: 1,
                                bgcolor: selected ? 'rgba(239, 68, 68, 0.08)' : '#1d2330',
                                cursor: 'pointer',
                                p: 1.25,
                                '&:hover': { borderColor: '#ef4444' },
                              }}
                            >
                              {/* Favoriet-ster (★) — rechtsboven, los klikbaar */}
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); toggleTplFav(template.id); }}
                                sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, p: 0.25,
                                  color: fav ? '#facc15' : 'rgba(148,163,184,0.7)',
                                  bgcolor: 'rgba(2,6,23,0.45)', '&:hover': { bgcolor: 'rgba(2,6,23,0.7)' } }}
                              >
                                {fav ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                              {(ritmoReady && template._k && window.RITMO?.renderCaption) ? (
                                <TemplateMiniPreview styleId={template.id} font={style.fontFamily} ready={ritmoReady} />
                              ) : (
                                <Box
                                  sx={{
                                    height: 78,
                                    borderRadius: 1,
                                    bgcolor: '#111827',
                                    display: 'grid',
                                    placeItems: 'center',
                                    mb: 1,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Typography
                                    fontWeight={900}
                                    sx={{
                                      color: '#fff',
                                      textAlign: 'center',
                                      lineHeight: 1.15,
                                      '& span': {
                                        color: (template.backgroundColor && template.backgroundColor !== 'transparent') ? '#fff' : template.activeColor,
                                        bgcolor: (template.backgroundColor && template.backgroundColor !== 'transparent') ? template.activeColor : 'transparent',
                                        borderRadius: template.kind === 'Box' ? '6px' : '3px',
                                        px: (template.backgroundColor && template.backgroundColor !== 'transparent') ? 0.6 : 0,
                                      },
                                    }}
                                  >
                                    Ritme <span>in</span><br />beeld
                                  </Typography>
                                </Box>
                              )}
                              <Typography fontWeight={800}>{template.name}</Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>{template.kind}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      {!filtered.length && (
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          {tplCat === '★ Favoriet' ? 'Nog geen favorieten — klik op een ster om er een toe te voegen.' : 'Geen templates in deze categorie.'}
                        </Typography>
                      )}
                    </>
                  );
                })()}

                {/* Eigen opgeslagen opmaak-templates — horen hier tussen de templates thuis
                    (stonden eerst alleen onderaan Opmaak, waar niemand ze terugvond). */}
                {savedTemplates.length > 0 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: 'rgba(148, 163, 184, 0.18)' }} />
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>
                      EIGEN TEMPLATES
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {savedTemplates.map((t) => (
                        <Stack key={t.name} direction="row" alignItems="center" spacing={1}
                          sx={{ bgcolor: '#1d2330', border: '1px solid rgba(148,163,184,.14)', borderRadius: 1, px: 1, py: 0.5 }}>
                          <StarIcon sx={{ fontSize: 15, color: '#facc15' }} />
                          <Typography variant="body2" sx={{ color: '#e5e7eb', flex: 1 }}>{t.name}</Typography>
                          <Button size="small" onClick={() => setStyle(s => ({ ...s, ...t.style }))}
                            sx={{ color: '#67e8c9', textTransform: 'none', minWidth: 'auto' }}>Toepassen</Button>
                          <IconButton size="small" onClick={() => persistTemplates(savedTemplates.filter(x => x.name !== t.name))}
                            sx={{ color: '#94a3b8', p: 0.25 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}

                <Divider sx={{ my: 2, borderColor: 'rgba(148, 163, 184, 0.18)' }} />

                {/* Template + video: importeren/exporteren en video vervangen (ondertitels behouden) */}
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  <input ref={templateImportRef} type="file" hidden accept=".json,application/json"
                    onChange={(e) => importTemplate(e.target.files?.[0])} />
                  <Button size="small" variant="outlined" onClick={exportTemplate}
                    sx={{ textTransform: 'none', color: '#67e8c9', borderColor: 'rgba(103,232,201,.4)' }}>
                    ⬇ Template exporteren
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => templateImportRef.current?.click()}
                    sx={{ textTransform: 'none', color: '#67e8c9', borderColor: 'rgba(103,232,201,.4)' }}>
                    ⬆ Template importeren
                  </Button>
                  <input ref={replaceVideoInputRef} type="file" hidden accept="video/*,audio/*"
                    onChange={(e) => replaceVideo(e.target.files?.[0])} />
                  <Button size="small" variant="outlined" onClick={() => replaceVideoInputRef.current?.click()}
                    disabled={!captions.length}
                    sx={{ textTransform: 'none', color: '#93c5fd', borderColor: 'rgba(147,197,253,.4)' }}>
                    🎬 Video vervangen (ondertitels behouden)
                  </Button>
                  <input ref={srtImportRef} type="file" hidden accept=".srt"
                    onChange={(e) => importSrt(e.target.files?.[0])} />
                  <Button size="small" variant="outlined" onClick={() => srtImportRef.current?.click()}
                    sx={{ textTransform: 'none', color: '#fca5a5', borderColor: 'rgba(252,165,165,.4)' }}>
                    📄 Ondertitels importeren (.srt)
                  </Button>
                </Stack>

                <Divider sx={{ my: 2, borderColor: 'rgba(148, 163, 184, 0.18)' }} />

                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TuneIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                    <Typography fontWeight={800}>Opmaak</Typography>
                  </Stack>
                  <TextField
                    label="Fontgrootte"
                    type="number"
                    size="small"
                    value={style.fontSize}
                    onChange={(event) => setStyle(prev => ({ ...prev, fontSize: Number(event.target.value) || 48 }))}
                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                    inputProps={{ min: 20, max: 120 }}
                    sx={{ '& input': { color: '#e5e7eb' }, '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.25)' } }}
                  />
                  <TextField
                    label="Woorden per regel"
                    type="number"
                    size="small"
                    value={style.wordsPerLine}
                    onChange={(event) => {
                      const value = Math.max(1, Math.min(12, Number(event.target.value) || 5));
                      setStyle(prev => ({ ...prev, wordsPerLine: value }));
                      setCaptions(prev => buildCaptions(prev.flatMap(caption => caption.words || []), value));
                    }}
                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                    inputProps={{ min: 1, max: 12 }}
                    sx={{ '& input': { color: '#e5e7eb' }, '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.25)' } }}
                  />
                  {/* Kleuren: tekst + accent (highlight). Overschrijven de template-defaults. */}
                  <Stack direction="row" spacing={1}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 0.5 }}>Tekstkleur</Typography>
                      <input
                        type="color"
                        value={style.textColor || '#ffffff'}
                        onChange={(e) => setStyle(prev => ({ ...prev, textColor: e.target.value }))}
                        style={{ width: '100%', height: 36, border: '1px solid rgba(148,163,184,0.25)', borderRadius: 6, background: 'transparent', cursor: 'pointer' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 0.5 }}>Accentkleur</Typography>
                      <input
                        type="color"
                        value={style.activeColor || '#67e8c9'}
                        onChange={(e) => setStyle(prev => ({ ...prev, activeColor: e.target.value }))}
                        style={{ width: '100%', height: 36, border: '1px solid rgba(148,163,184,0.25)', borderRadius: 6, background: 'transparent', cursor: 'pointer' }}
                      />
                    </Box>
                  </Stack>
                  <Button
                    variant={style.uppercase ? 'contained' : 'outlined'}
                    onClick={() => setStyle(prev => ({ ...prev, uppercase: !prev.uppercase }))}
                    sx={{ justifyContent: 'flex-start', color: style.uppercase ? '#042f2e' : '#e5e7eb', borderColor: 'rgba(148, 163, 184, 0.35)' }}
                  >
                    Hoofdletters
                  </Button>
                </Stack>

                <Divider sx={{ my: 2, borderColor: 'rgba(148, 163, 184, 0.18)' }} />

                {/* Merk-presets — meteen on-brand kleuren. */}
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>
                  MERK
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {BRAND_PRESETS.map(preset => (
                    <Button
                      key={preset.id}
                      size="small"
                      variant="outlined"
                      onClick={() => applyBrandPreset(preset)}
                      sx={{
                        textTransform: 'none',
                        color: '#e5e7eb',
                        borderColor: 'rgba(148, 163, 184, 0.35)',
                        '&:hover': { borderColor: '#a855f7' },
                      }}
                    >
                      <Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: preset.activeColor, mr: 0.75, border: '1px solid rgba(255,255,255,0.4)' }} />
                      {preset.name}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}

            {rightTab === 'opmaak' && (() => {
              const setSF = (k, v) => setStyle(s => ({ ...s, [k]: v }));
              const tog = (k) => (e) => setSF(k, e.target.checked);
              return (
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>LETTERTYPE</Typography>
                  <Box sx={{ mt: 1, mb: 1.5 }}>
                    <Box component="select" value={style.fontFamily || 'Inter'}
                      onChange={(e) => setSF('fontFamily', e.target.value)}
                      sx={{ width: '100%', bgcolor: '#0f1420', color: '#e5e7eb', border: '1px solid #2b3445',
                        borderRadius: 1, px: 1, py: 0.75, fontSize: 14, '&:focus': { outline: 'none', borderColor: '#67e8c9' } }}>
                      <optgroup label="Standaard">
                        {FONT_OPTIONS.map(f => <option key={f.name} value={f.name} style={{ fontFamily: f.name }}>{f.name}</option>)}
                      </optgroup>
                      {uploadedFonts.length > 0 && (
                        <optgroup label="Geüpload (huisstijl/klant)">
                          {uploadedFonts.map(f => <option key={f.filename} value={f.name} style={{ fontFamily: f.name }}>{f.name}</option>)}
                        </optgroup>
                      )}
                      {customFonts.length > 0 && (
                        <optgroup label="Eigen / Adobe">
                          {customFonts.map(n => <option key={n} value={n} style={{ fontFamily: n }}>{n}</option>)}
                        </optgroup>
                      )}
                    </Box>
                    {!fontAvailable(style.fontFamily || 'Inter') && (
                      <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block', mt: 0.5 }}>
                        ⚠ "{style.fontFamily}" lijkt niet beschikbaar in de browser — de preview en render vallen terug op een systeemfont. Laad het via een Adobe-kit hieronder.
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} alignItems="center">
                      <Box component="input" placeholder="Adobe Fonts kit-id (bv. abc1def)" value={adobeKit}
                        onChange={(e) => setAdobeKit(e.target.value.trim())}
                        sx={{ flex: 1, bgcolor: '#0f1420', color: '#e5e7eb', border: '1px solid #2b3445', borderRadius: 1,
                          px: 1, py: 0.5, fontSize: 12, '&:focus': { outline: 'none', borderColor: '#67e8c9' } }} />
                      <Button size="small" onClick={openFontDialog} sx={{ color: '#cbd5e1', textTransform: 'none', whiteSpace: 'nowrap' }}>+ Eigen font</Button>
                      <Button size="small" onClick={() => fontUploadRef.current?.click()} sx={{ color: '#67e8c9', textTransform: 'none', whiteSpace: 'nowrap' }}>⬆ Upload .ttf/.otf</Button>
                      <input ref={fontUploadRef} type="file" accept=".ttf,.otf" hidden onChange={(e) => { uploadFont(e.target.files?.[0]); e.target.value = ''; }} />
                    </Stack>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                      Adobe Fonts: maak een web-project aan, plak het kit-id, voeg dan de fontnaam toe met "+ Eigen font".
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }}>
                    <FormControlLabel control={<Checkbox size="small" checked={!!style.bold} onChange={tog('bold')} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />} label={<b style={{ color: '#cbd5e1' }}>Bold</b>} />
                    <FormControlLabel control={<Checkbox size="small" checked={!!style.italic} onChange={tog('italic')} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />} label={<i style={{ color: '#cbd5e1' }}>Cursief</i>} />
                    <FormControlLabel control={<Checkbox size="small" checked={!!style.underline} onChange={tog('underline')} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />} label={<u style={{ color: '#cbd5e1' }}>Onderstreept</u>} />
                  </Stack>

                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>KLEUREN</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
                    <StudioColor label="Basis" value={style.textColor} onChange={(v) => setSF('textColor', v)} />
                    <StudioColor label="Actief" value={style.activeColor} onChange={(v) => setSF('activeColor', v)} />
                    <StudioColor label="Gesproken" value={style.pastColor} onChange={(v) => setSF('pastColor', v)} />
                    <StudioColor label="Nadruk" value={style.emphColor} onChange={(v) => setSF('emphColor', v)} />
                  </Stack>

                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>VORM &amp; POSITIE</Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <StudioSlider label="Grootte" value={style.fontSize} min={16} max={160} onChange={(v) => setSF('fontSize', v)} />
                    <StudioSlider label="Horizontale positie" value={style.posX ?? 50} min={0} max={100} suffix="%" onChange={(v) => setSF('posX', v)} />
                    <StudioSlider label="Verticale positie" value={style.posY ?? 82} min={0} max={100} suffix="%" onChange={(v) => setSF('posY', v)} />
                    <StudioSlider label="Woorden per regel" value={style.wordsPerLine} min={1} max={12} onChange={(v) => {
                      const wpl = Math.max(1, Math.min(12, Math.round(v)));
                      setSF('wordsPerLine', wpl);
                      // Regels ook echt opnieuw opbouwen (anders verandert alleen de waarde, niet de weergave).
                      setCaptions(prev => buildCaptions(prev.flatMap(c => c.words || []), wpl));
                    }} />
                    <StudioSlider label="Regelafstand" value={style.lineGap ?? 15} min={0} max={100} suffix="%" onChange={(v) => setSF('lineGap', v)} />
                    <StudioSlider label="Woordafstand" value={style.wordGap ?? 28} min={0} max={100} suffix="%" onChange={(v) => setSF('wordGap', v)} />
                    <StudioSlider label="Rand (stroke)" value={style.stroke ?? 0} min={0} max={20} onChange={(v) => setSF('stroke', v)} />
                    <StudioSlider label="Nadruk-grootte" value={style.emphScale ?? 122} min={100} max={220} suffix="%" onChange={(v) => setSF('emphScale', v)} />
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Box sx={{ flex: 1 }}><StudioColor label="Rand-kleur" value={style.strokeColor} onChange={(v) => setSF('strokeColor', v)} /></Box>
                      <Box sx={{ flex: 2 }} />
                    </Stack>
                    <FormControlLabel control={<Checkbox size="small" checked={!!style.uppercase} onChange={tog('uppercase')} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />} label={<span style={{ color: '#cbd5e1' }}>Hoofdletters</span>} />
                    <FormControlLabel control={<Checkbox size="small" checked={!!style.noMotion} onChange={tog('noMotion')} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />} label={<span style={{ color: '#cbd5e1' }}>Geen pop/beweging</span>} />
                  </Box>

                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>ACHTERGROND-BOX</Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <FormControlLabel control={<Checkbox size="small" checked={!!style.boxOn} onChange={tog('boxOn')} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />} label={<span style={{ color: '#cbd5e1' }}>Achtergrond aan</span>} />
                    <Stack direction="row" spacing={1} sx={{ my: 1 }}>
                      <StudioColor label="Kleur" value={style.boxColor} onChange={(v) => setSF('boxColor', v)} />
                      <Box sx={{ flex: 2 }} />
                    </Stack>
                    <StudioSlider label="Dekking" value={style.boxOpacity ?? 60} min={0} max={100} suffix="%" onChange={(v) => setSF('boxOpacity', v)} />
                    <StudioSlider label="Hoekradius" value={style.boxRadius ?? 18} min={0} max={100} suffix="%" onChange={(v) => setSF('boxRadius', v)} />
                    <StudioSlider label="Padding" value={style.boxPad ?? 20} min={0} max={80} suffix="%" onChange={(v) => setSF('boxPad', v)} />
                  </Box>

                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>SCHADUW &amp; GLOED</Typography>
                  <Box sx={{ mt: 1 }}>
                    <StudioSlider label="Schaduw" value={style.shadow ?? 40} min={0} max={100} suffix="%" onChange={(v) => setSF('shadow', v)} />
                    <StudioSlider label="Gloed" value={style.glow ?? 0} min={0} max={100} suffix="%" onChange={(v) => setSF('glow', v)} />
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <StudioColor label="Schaduwkleur" value={style.shadowColor} onChange={(v) => setSF('shadowColor', v)} />
                      <StudioColor label="Gloedkleur" value={style.glowColor} onChange={(v) => setSF('glowColor', v)} />
                    </Stack>
                  </Box>

                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>TRANSCRIPT &amp; TIMING</Typography>
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel control={<Checkbox size="small" checked={markLowConf} onChange={(e) => setMarkLowConf(e.target.checked)} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />}
                      label={<span style={{ color: '#cbd5e1' }}>Onzekere woorden markeren <span style={{ color: '#FFB020' }}>(oranje streep)</span></span>} />
                    <StudioSlider label="Inloop vóór eerste woord" value={Math.round((style.lead ?? 0.12) * 100)} min={0} max={100} step={2} suffix=" cs" onChange={(v) => setSF('lead', v / 100)} />
                    <StudioSlider label="Uitloop ná laatste woord" value={Math.round((style.tail ?? 0) * 100)} min={0} max={150} step={5} suffix=" cs" onChange={(v) => setSF('tail', v / 100)} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Hoe lang een regel vóór/na de gesproken woorden zichtbaar blijft (cs = honderdsten van een seconde). Werkt op preview én export.</Typography>
                  </Box>

                  {/* Eigen templates: huidige opmaak opslaan per klant/naam, weer oproepbaar. */}
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>EIGEN TEMPLATES</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<StarIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        const name = (window.prompt('Sla deze opmaak op als template (bv. klantnaam):', '') || '').trim();
                        if (!name) return;
                        const next = [...savedTemplates.filter(t => t.name !== name), { name, style }];
                        persistTemplates(next);
                      }}
                      sx={{ color: '#e5e7eb', borderColor: 'rgba(148,163,184,.35)', textTransform: 'none', mb: 1 }}>
                      Huidige opmaak opslaan
                    </Button>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      {savedTemplates.length === 0
                        ? 'Nog geen opgeslagen templates.'
                        : `Je ${savedTemplates.length === 1 ? 'template staat' : `${savedTemplates.length} templates staan`} in het Templates-tabblad (onder "Eigen templates").`}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}

            {rightTab === 'text' && (
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <SubtitlesIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                  <Typography fontWeight={800}>Transcript</Typography>
                  <Chip size="small" label={`${allWords.length} woorden`} sx={{ ml: 'auto', color: '#cbd5e1', bgcolor: '#222a38' }} />
                </Stack>
                {/* Spellingcontrole (NL) — corrigeert alleen duidelijke spel-/typefouten, behoudt timing. */}
                <Button size="small" variant="outlined" onClick={runSpellCheck}
                  disabled={spellBusy || !captions.length}
                  startIcon={spellBusy ? <CircularProgress size={14} sx={{ color: '#67e8c9' }} /> : <SpellcheckIcon fontSize="small" />}
                  sx={{ color: '#67e8c9', borderColor: 'rgba(103,232,201,.4)', textTransform: 'none', alignSelf: 'flex-start' }}>
                  {spellBusy ? 'Bezig met controleren…' : 'Spelling checken'}
                </Button>

                {captions.length ? (
                  <Stack spacing={1}>
                    {/* Actiebalk: werkt op het geselecteerde woord (klik of rechterklik). */}
                    <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: '#11151f', pb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 0.5 }}>
                        Klik een woord (of rechterklik), dan:
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" disabled={!selWord}
                          onClick={() => selWord && splitAtWord(selWord.ci, selWord.wi)}
                          sx={{ color: '#e5e7eb', borderColor: 'rgba(148,163,184,.3)', textTransform: 'none' }}>Splitsen hier</Button>
                        <Button size="small" variant="outlined" disabled={!selWord}
                          onClick={() => selWord && isolateWord(selWord.ci, selWord.wi)}
                          sx={{ color: '#e5e7eb', borderColor: 'rgba(148,163,184,.3)', textTransform: 'none' }}>Eigen regel</Button>
                        <Button size="small" variant="outlined" disabled={!selWord}
                          onClick={() => selWord && toggleEmph(selWord.ci, selWord.wi)}
                          sx={{ color: '#e5e7eb', borderColor: 'rgba(148,163,184,.3)', textTransform: 'none' }}>Nadruk</Button>
                      </Stack>
                    </Box>
                    {captions.map((caption, ci) => {
                      const chars = (caption.text || '').length;
                      const dur = Math.max(0.01, (caption.end || 0) - (caption.start || 0));
                      const cps = chars / dur;
                      const cpsColor = cps >= 24 ? '#f87171' : cps >= 18 ? '#fbbf24' : '#4ade80';
                      const isActiveLine = activeCaption === caption;
                      return (
                      <Box key={`${caption.start}-${ci}`}
                        sx={{ p: 1.25, borderRadius: 1, bgcolor: isActiveLine ? 'rgba(103, 232, 201, 0.10)' : '#1d2330',
                          border: isActiveLine ? '1px solid rgba(103,232,201,.5)' : '1px solid rgba(148, 163, 184, 0.12)' }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>regel {ci + 1}</Typography>
                          <Typography variant="caption" sx={{ color: cpsColor, fontWeight: 700, fontFamily: 'monospace' }}>
                            {chars} tkns · {cps.toFixed(1)} CPS
                          </Typography>
                          {(caption.posX != null || caption.posY != null) && (
                            <Chip size="small" label={caption.posOvergenomen ? '📍 overgenomen ▾' : '📍 eigen positie ▾'}
                              onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setPosMenu({ x: r.left, y: r.bottom, ci }); }}
                              title="Deze regel heeft een eigen positie in beeld — klik om door te zetten naar andere regels of terug te zetten"
                              sx={{ height: 18, fontSize: 10, cursor: 'pointer', color: '#67e8c9', bgcolor: 'rgba(103,232,201,.12)' }} />
                          )}
                          <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace', ml: 'auto' }}>
                            {formatClock(caption.start)} – {formatClock(caption.end)}
                          </Typography>
                        </Stack>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                          onDragOver={(e) => { if (dragWordRef.current) e.preventDefault(); }}
                          onDrop={(e) => { e.preventDefault(); moveWord(dragWordRef.current, { ci, wi: null }); dragWordRef.current = null; }}>
                          {(caption.words || []).map((w, wi) => {
                            const sel = selWord && selWord.ci === ci && selWord.wi === wi;
                            return (
                              <Box key={`${wi}-${w.start}`} component="span"
                                draggable
                                onDragStart={(e) => { dragWordRef.current = { ci, wi }; e.dataTransfer.effectAllowed = 'move'; }}
                                onDragEnd={() => { dragWordRef.current = null; }}
                                onDragOver={(e) => { if (dragWordRef.current) { e.preventDefault(); e.stopPropagation(); } }}
                                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); moveWord(dragWordRef.current, { ci, wi }); dragWordRef.current = null; }}
                                onClick={() => { setSelWord({ ci, wi }); if (videoRef.current) videoRef.current.currentTime = w.start; setCurrentTime(w.start); }}
                                onDoubleClick={() => editWordText(ci, wi)}
                                onContextMenu={(e) => openWordMenu(e, ci, wi)}
                                title="Klik = selecteren · dubbelklik = tekst wijzigen · sleep = naar andere regel · rechterklik = menu"
                                sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'grab', fontSize: 14, userSelect: 'none',
                                  '&:active': { cursor: 'grabbing' },
                                  bgcolor: sel ? '#26e0c8' : '#0f1420', color: sel ? '#04201c' : '#e5e7eb',
                                  border: w.emph ? '2px solid #67e8c9' : '1px solid rgba(148,163,184,.22)',
                                  borderLeft: w.br ? '3px solid #26e0c8' : undefined,
                                  fontWeight: w.emph ? 800 : 500, '&:hover': { borderColor: '#67e8c9' } }}>
                                {w.text}
                              </Box>
                            );
                          })}
                          <Box component="span" onClick={() => addWordToCaption(ci)}
                            sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', fontSize: 13, color: '#64748b',
                              border: '1px dashed rgba(148,163,184,.4)', '&:hover': { color: '#cbd5e1', borderColor: '#67e8c9' } }}>
                            + woord
                          </Box>
                        </Box>
                      </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ bgcolor: '#172033', color: '#dbeafe' }}>
                    Start spraakherkenning om hier de woorden en timing te zien.
                  </Alert>
                )}
              </Stack>
            )}

            {rightTab === 'export' && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>RENDER-OPTIES</Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <FormControlLabel control={<Checkbox size="small" checked={exportFast} onChange={(e) => setExportFast(e.target.checked)} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />}
                    label={<span style={{ color: '#cbd5e1' }}>Snelle render (lagere resolutie, om te testen)</span>} />
                  <FormControlLabel control={<Checkbox size="small" checked={exportRangeOnly} onChange={(e) => setExportRangeOnly(e.target.checked)}
                    disabled={inPoint == null || outPoint == null} sx={{ color: '#67e8c9', '&.Mui-checked': { color: '#67e8c9' } }} />}
                    label={<span style={{ color: '#cbd5e1' }}>Alleen fragment (in-/uit-punt{inPoint != null && outPoint != null ? `: ${formatClock(inPoint)}–${formatClock(outPoint)}` : ' — zet I/O op de tijdlijn'})</span>} />
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>Renderresolutie (langste zijde)</Typography>
                    <Select fullWidth size="small" value={exportScale} onChange={(e) => setExportScale(e.target.value)}
                      MenuProps={{ sx: { zIndex: 3000 } }}
                      sx={{ mt: 0.5, color: '#e5e7eb', bgcolor: '#1d2330', '& .MuiSelect-icon': { color: '#94a3b8' } }}>
                      <MenuItem value="">Origineel</MenuItem>
                      <MenuItem value="3840">3840 (4K)</MenuItem>
                      <MenuItem value="1920">1920 (1080p)</MenuItem>
                      <MenuItem value="1280">1280 (720p)</MenuItem>
                      <MenuItem value="854">854 (480p)</MenuItem>
                    </Select>
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>MP4 — kwaliteit / platform</Typography>
                    <Select fullWidth size="small" value={mp4Preset} onChange={(e) => setMp4Preset(e.target.value)}
                      MenuProps={{ sx: { zIndex: 3000 } }}
                      sx={{ mt: 0.5, color: '#e5e7eb', bgcolor: '#1d2330', '& .MuiSelect-icon': { color: '#94a3b8' } }}>
                      <MenuItem value="youtube">YouTube (hoogste bitrate)</MenuItem>
                      <MenuItem value="instagram">Instagram Reels</MenuItem>
                      <MenuItem value="tiktok">TikTok</MenuItem>
                    </Select>
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>Opslaglocatie op de render-server (optioneel)</Typography>
                    <Box component="input" placeholder="bv. /Users/pepijn/Desktop/  (leeg = standaardmap)"
                      value={outputDir}
                      onChange={(e) => { setOutputDir(e.target.value); try { window.localStorage.setItem('ondertitelstudio_outdir', e.target.value); } catch { /* ignore */ } }}
                      sx={{ width: '100%', mt: 0.5, bgcolor: '#1d2330', color: '#e5e7eb', border: '1px solid #2b3445',
                        borderRadius: 1, px: 1, py: 0.6, fontSize: 13, '&:focus': { outline: 'none', borderColor: '#67e8c9' } }} />
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                      Pad op de render-server (Mac) waar het bestand wordt neergezet, bv. een
                      gemounte netwerkschijf. Leeg = standaard render-map. Tip: kies een gedeelde
                      map (server) zodat je 'm daarna direct in Finder/Verkenner ziet i.p.v. lokaal downloaden.
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>VIDEO (INGEBRAND)</Typography>
                <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
                  {RITMO_VIDEO_EXPORT_ENABLED && (
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => exportVideo('mp4')}
                      disabled={!captions.length || renderJob?.status === 'running'}
                      sx={{ background: '#67e8c9', color: '#042f2e', fontWeight: 700, '&:hover': { background: '#5eead4' } }}>
                      MP4 renderen ({mp4Preset})
                    </Button>
                  )}
                  {RITMO_VIDEO_EXPORT_ENABLED && (
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" onClick={() => exportVideo('prores')} disabled={!captions.length || renderJob?.status === 'running'} sx={{ flex: 1, color: '#e5e7eb', borderColor: 'rgba(148,163,184,.35)' }}>ProRes 4444 (video + titels)</Button>
                      <Tooltip title="Alleen de ondertitels op transparante achtergrond als ProRes 4444 — leg je direct over je edit">
                        <Button variant="outlined" onClick={() => exportVideo('prores-alpha')} disabled={!captions.length || renderJob?.status === 'running'} sx={{ flex: 1, color: '#e5e7eb', borderColor: 'rgba(148,163,184,.35)' }}>Alleen titels — transparant (ProRes 4444)</Button>
                      </Tooltip>
                    </Stack>
                  )}
                  {renderJob?.status === 'running' && (
                    <Typography variant="caption" sx={{ color: '#67e8c9' }}>Renderen… {Math.round((renderJob.progress || 0) * 100)}%</Typography>
                  )}
                  {renderJob?.status === 'done' && (
                    <Stack spacing={0.5}>
                      {renderJob.result?.download && (
                        <Button variant="contained" href={renderJob.result.download} target="_blank" rel="noopener noreferrer" startIcon={<DownloadIcon />} sx={{ background: '#22c55e', color: '#04210f', fontWeight: 700 }}>Download video</Button>
                      )}
                      {/* Toon waar het bestand op de render-server staat (Finder/Verkenner) */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          📁 Op de render-server: <b style={{ color: '#cbd5e1' }}>{renderJob.result?.outputPath || renderJob.result?.savedTo || (outputDir || '').trim() || 'standaard render-map'}</b>
                        </Typography>
                        {(renderJob.result?.outputPath || renderJob.result?.savedTo || (outputDir || '').trim()) && (
                          <Button size="small" onClick={() => { try { navigator.clipboard.writeText(renderJob.result?.outputPath || renderJob.result?.savedTo || outputDir.trim()); showNotice('Pad gekopieerd', 'success'); } catch { /* ignore */ } }}
                            sx={{ color: '#67e8c9', textTransform: 'none', minWidth: 0, py: 0 }}>kopieer pad</Button>
                        )}
                      </Box>
                    </Stack>
                  )}
                </Stack>

                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: 1 }}>ONDERTITELBESTANDEN</Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1 }}>
                  Normale ondertiteling: regelafbreking naar max 2 regels (per beeldverhouding) + puntjes bij zinsafbreking.
                </Typography>
                <Box sx={{ mt: 0.5, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>Puntjes bij zinsafbreking</Typography>
                  <Box component="select" value={subEllipsis} onChange={(e) => setSubEllipsis(e.target.value)}
                    sx={{ width: '100%', mt: 0.5, color: '#e5e7eb', bgcolor: '#1d2330', border: '1px solid rgba(148,163,184,.35)', borderRadius: 1, p: 1, fontSize: 13, '& option': { color: '#111' } }}>
                    <option value="none">Geen puntjes (Social Media)</option>
                    <option value="end_only">Alleen einde (NPO/Omroep)</option>
                    <option value="both">Begin en einde (Netflix/RTL)</option>
                  </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={() => exportSubtitles('srt')} disabled={!captions.length} sx={{ flex: 1, color: '#e5e7eb', borderColor: 'rgba(148,163,184,.35)' }}>.srt</Button>
                  <Button variant="outlined" onClick={() => exportSubtitles('vtt')} disabled={!captions.length} sx={{ flex: 1, color: '#e5e7eb', borderColor: 'rgba(148,163,184,.35)' }}>.vtt</Button>
                  <Button variant="outlined" onClick={exportFcpxml} disabled={!captions.length} sx={{ flex: 1, color: '#e5e7eb', borderColor: 'rgba(148,163,184,.35)' }}>FCPXML</Button>
                </Stack>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 2 }}>
                  Render-opties (fragment/snel/resolutie/preset) gaan mee naar de render-server. Spellingcontrole staat in het tabblad <b>Tekst</b>. Vertalen (DeepL) is nog niet gekoppeld.
                </Typography>
              </Box>
            )}

            {rightTab === 'projects' && (
              <Stack spacing={1.25}>
                <Stack direction="row" alignItems="center">
                  <Typography fontWeight={800}>Opgeslagen projecten</Typography>
                  {loadingProjects && <CircularProgress size={16} sx={{ ml: 'auto', color: '#67e8c9' }} />}
                </Stack>
                <TextField
                  size="small" fullWidth placeholder="Zoek op naam of klant…"
                  value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': { bgcolor: '#1d2330', color: '#e2e8f0', fontSize: 14 },
                    '& fieldset': { borderColor: 'rgba(148,163,184,0.2)' },
                  }}
                />
                {projects.length ? (() => {
                  // Groepeer op klantnaam-conventie: "Klant - titel" / "Klant | titel"
                  const q = projectSearch.trim().toLowerCase();
                  const filtered = projects.filter(p => !q || (p.name || '').toLowerCase().includes(q));
                  const groups = new Map();
                  for (const p of filtered) {
                    const m = String(p.name || '').match(/^(.{2,40}?)\s*[-–|]\s+/);
                    const key = m ? m[1].trim() : 'Overig';
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key).push(p);
                  }
                  const sortedGroups = [...groups.entries()].sort((a, b) =>
                    a[0] === 'Overig' ? 1 : b[0] === 'Overig' ? -1 : a[0].localeCompare(b[0]));
                  if (!filtered.length) {
                    return <Typography variant="caption" sx={{ color: '#64748b', p: 1 }}>Geen projecten gevonden voor “{projectSearch}”.</Typography>;
                  }
                  return sortedGroups.map(([client, items]) => (
                    <Box key={client}>
                      <Typography
                        onClick={() => setCollapsedClients(prev => ({ ...prev, [client]: !prev[client] }))}
                        sx={{ fontSize: 12, fontWeight: 800, color: '#8b97a8', textTransform: 'uppercase',
                              letterSpacing: '0.06em', cursor: 'pointer', py: 0.5, userSelect: 'none' }}>
                        {collapsedClients[client] && !q ? '▸' : '▾'} {client} <span style={{ fontWeight: 500 }}>({items.length})</span>
                      </Typography>
                      {(!collapsedClients[client] || q) && items
                        .sort((a, b) => (b.updated || 0) - (a.updated || 0))
                        .map(project => (
                        <Box
                          key={project.id}
                          onClick={() => loadProject(project.id)}
                          sx={{
                            p: 1.25, mb: 0.75,
                            borderRadius: 1,
                            bgcolor: project.id === projectId ? 'rgba(103, 232, 201, 0.12)' : '#1d2330',
                            border: '1px solid rgba(148, 163, 184, 0.12)',
                            cursor: 'pointer',
                          }}
                        >
                          <Typography fontWeight={800}>{project.name}</Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {project.updated ? new Date(project.updated * 1000).toLocaleString('nl-NL') : 'Geen datum'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ));
                })() : (
                  <Alert severity="info" sx={{ bgcolor: '#172033', color: '#dbeafe' }}>
                    Nog geen opgeslagen Ritmo-projecten.
                  </Alert>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          minHeight: 0,
          borderTop: '1px solid rgba(148, 163, 184, 0.18)',
          bgcolor: '#11151f',
          display: 'grid',
          gridTemplateRows: '44px 1fr',
        }}
      >
        {/* Sleep-greep om de tijdlijn hoger/lager te maken (viewer schaalt mee) — item 9. */}
        <Box onMouseDown={startTimelineResize}
          sx={{ position: 'absolute', top: -3, left: 0, right: 0, height: 8, cursor: 'ns-resize', zIndex: 6,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            '&:hover .grip': { bgcolor: '#67e8c9' } }}>
          <Box className="grip" sx={{ width: 46, height: 3, borderRadius: 2, bgcolor: 'rgba(148,163,184,.5)' }} />
        </Box>
        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" fontWeight={900} sx={{ color: '#64748b', letterSpacing: 1 }}>
            TIJDLIJN
          </Typography>
          {hasVideo && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 1 }}>
              <Tooltip title="Hand-tool: sleep om door de tijdlijn te schuiven (H)">
                <IconButton size="small" onClick={() => setHandTool(t => !t)}
                  sx={{ color: handTool ? '#042f2e' : '#cbd5e1', bgcolor: handTool ? '#67e8c9' : 'transparent', border: '1px solid rgba(148,163,184,.25)', borderRadius: 1, width: 26, height: 26 }}>✋</IconButton>
              </Tooltip>
              <Tooltip title="Uitzoomen (−)"><IconButton size="small" onClick={() => zoomTl(1 / 1.3)} sx={{ color: '#cbd5e1', border: '1px solid rgba(148,163,184,.25)', borderRadius: 1, width: 26, height: 26 }}>−</IconButton></Tooltip>
              <Tooltip title="Inzoomen (+)"><IconButton size="small" onClick={() => zoomTl(1.3)} sx={{ color: '#cbd5e1', border: '1px solid rgba(148,163,184,.25)', borderRadius: 1, width: 26, height: 26 }}>+</IconButton></Tooltip>
              <Tooltip title="In-punt op de afspeelkop (I)"><Button size="small" onClick={() => setInPoint(currentTime)} sx={{ color: '#cbd5e1', minWidth: 30 }}>I</Button></Tooltip>
              <Tooltip title="Uit-punt op de afspeelkop (O)"><Button size="small" onClick={() => setOutPoint(currentTime)} sx={{ color: '#cbd5e1', minWidth: 30 }}>O</Button></Tooltip>
              {(inPoint != null || outPoint != null) && (
                <Tooltip title="Fragment wissen"><Button size="small" onClick={() => { setInPoint(null); setOutPoint(null); }} sx={{ color: '#fca5a5', minWidth: 30 }}>✕</Button></Tooltip>
              )}
            </Stack>
          )}
          {job && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1, minWidth: 280 }}>
              {job.status === 'error' ? <ErrorOutlineIcon color="error" fontSize="small" /> : job.status === 'done' ? <CheckCircleIcon color="success" fontSize="small" /> : <AutoAwesomeIcon fontSize="small" sx={{ color: '#67e8c9' }} />}
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: '#cbd5e1' }}>{job.message || job.error || 'Bezig...'}</Typography>
                {job.status === 'running' && (
                  <LinearProgress
                    variant="determinate"
                    value={(job.progress || 0) * 100}
                    sx={{ height: 4, borderRadius: 999, bgcolor: '#1f2937', '& .MuiLinearProgress-bar': { bgcolor: '#67e8c9' } }}
                  />
                )}
              </Box>
            </Stack>
          )}
          {/* Export-knoppen staan nu gebundeld in de Export-tab. Hier alleen render-
              status + een snelle download als 'ie klaar is. */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
            {renderJob?.status === 'running' && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#67e8c9' }}>
                <CircularProgress size={14} color="inherit" />
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Renderen… {Math.round((renderJob.progress || 0) * 100)}%</Typography>
              </Stack>
            )}
            {renderJob?.status === 'done' && renderJob.result?.download && (
              <Button size="small" variant="contained" href={renderJob.result.download} target="_blank" rel="noopener noreferrer"
                startIcon={<DownloadIcon fontSize="small" />} sx={{ background: '#22c55e', color: '#04210f', fontWeight: 700, whiteSpace: 'nowrap' }}>
                Download video
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />}
              onClick={() => setRightTab('export')} disabled={!captions.length}
              sx={{ color: '#e5e7eb', borderColor: 'rgba(148,163,184,.35)', whiteSpace: 'nowrap' }}>
              Exporteren →
            </Button>
          </Stack>
        </Box>

        {/* pr op lg = breedte van het rechter Templates-paneel (400px), zodat de tijdlijn
            dezelfde breedte/uitlijning heeft als de controlebalk-scrubber erboven → de rode
            cursor valt samen met het uiteinde van de voortgangsbalk. */}
        <Box ref={tlScrollRef} sx={{ position: 'relative', minHeight: 0, overflowX: 'auto', px: 2, pb: 2, pr: { lg: '400px' } }}>
          {duration > 0 && (
            <Box
              ref={tlRulerRef}
              onMouseDown={(e) => { if (e.button !== 0 || e.ctrlKey) return; if (hasVideo) (handTool ? startTimelinePan : ((ev) => seekFromPointer(ev.clientX, e.currentTarget)))(e); }}
              sx={{ position: 'sticky', top: 0, height: 26, minWidth: 900 * tlZoom, zIndex: 3,
                bgcolor: '#11151f', borderBottom: '1px solid rgba(148,163,184,.15)',
                cursor: hasVideo ? (handTool ? 'grab' : 'pointer') : 'default' }}>
              {/* Tijd-markeringen (ticks + labels) — passen zich aan de zoom aan. */}
              {(() => {
                const pxW = 900 * tlZoom;
                const pps = pxW / duration;                       // pixels per seconde
                const steps = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300];
                const step = steps.find(s => s * pps >= 64) || 600;
                const ticks = [];
                for (let t = 0; t <= duration + 0.001; t += step) ticks.push(t);
                return ticks.map((t) => (
                  <Box key={t} sx={{ position: 'absolute', left: `${(t / duration) * 100}%`, top: 0, bottom: 0, pointerEvents: 'none' }}>
                    <Box sx={{ position: 'absolute', left: 0, top: 14, bottom: 0, width: '1px', bgcolor: 'rgba(148,163,184,.35)' }} />
                    <Typography sx={{ position: 'absolute', left: 3, top: 2, fontSize: 9.5, color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatClock(t)}
                    </Typography>
                  </Box>
                ));
              })()}
              {/* Horizontale voortgangslijn (0 → huidige tijd). */}
              <Box sx={{ position: 'absolute', left: 0, width: `${Math.min(100, (currentTime / duration) * 100)}%`,
                bottom: 0, height: 2, bgcolor: '#ef4444', zIndex: 2, pointerEvents: 'none' }} />
              {/* Verticale afspeelcursor (loopt door over de track eronder). */}
              <Box sx={{ position: 'absolute', left: `${Math.min(100, (currentTime / duration) * 100)}%`,
                top: 0, bottom: -132, width: 2, bgcolor: '#ef4444', zIndex: 2, pointerEvents: 'none' }} />
            </Box>
          )}

          <Box
            ref={tlTrackRef}
            onMouseDown={(e) => { if (e.button !== 0 || e.ctrlKey) return; if (e.target === e.currentTarget) setSelWord(null); if (hasVideo && duration) (handTool ? startTimelinePan : startTimelineScrub)(e); }}
            onContextMenu={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }}
            onDoubleClick={(e) => {
              if (e.target !== e.currentTarget || !hasVideo || !duration) return;
              const rect = e.currentTarget.getBoundingClientRect();
              addWordAtTime(Math.max(0, ((e.clientX - rect.left) / rect.width) * duration));
            }}
            sx={{ minWidth: 900 * tlZoom, height: 118, position: 'relative', cursor: hasVideo && duration ? (handTool ? 'grab' : 'pointer') : 'default' }}
          >
            {/* Waveform achter de blokken — spant 100% van de (zoom-)track. */}
            <Box component="canvas" ref={waveCanvasRef} height={104}
              sx={{ position: 'absolute', left: 0, top: 7, width: '100%', height: 104, pointerEvents: 'none', zIndex: 0 }} />
            {/* Fragment (in-/uit-punt) als gearceerde band. */}
            {duration > 0 && (inPoint != null || outPoint != null) && (
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, zIndex: 1, pointerEvents: 'none',
                left: `${Math.max(0, ((inPoint ?? 0) / duration) * 100)}%`,
                width: `${Math.max(0, (((outPoint ?? duration) - (inPoint ?? 0)) / duration) * 100)}%`,
                bgcolor: 'rgba(239,68,68,.12)', borderLeft: '2px solid #ef4444', borderRight: '2px solid #ef4444' }} />
            )}
            {captions.length ? (
              captions.flatMap((caption, ci) => (caption.words || []).map((w, wi) => {
                const left = duration ? (w.start / duration) * 100 : 0;
                const width = duration ? Math.max(0.8, ((w.end - w.start) / duration) * 100) : 2;
                const lane = ci % 2;
                const isSel = selWord && selWord.ci === ci && selWord.wi === wi;
                const isActive = currentTime >= w.start && currentTime <= w.end;
                return (
                  <Box
                    key={`${ci}-${wi}-${w.start}`}
                    onMouseDown={(e) => startWordDrag(e, ci, wi, 'move')}
                    onClick={(e) => { e.stopPropagation(); setSelWord({ ci, wi }); if (tlTrackRef.current) seekFromPointer(e.clientX, tlTrackRef.current); else seekTo(w.start); }}
                    onContextMenu={(e) => openWordMenu(e, ci, wi)}
                    onDoubleClick={(e) => e.stopPropagation()}
                    title={`${w.text} — sleep = verschuif · randen = timing · E nadruk · S splitsen · Enter regelafbreking · ⌫ verwijderen`}
                    sx={{
                      position: 'absolute', left: `${left}%`, width: `${width}%`,
                      top: lane ? 56 : 12, minWidth: 22, height: 34, borderRadius: 1,
                      bgcolor: isActive ? '#26e0c8' : '#202736',
                      color: isActive ? '#04201c' : '#e5e7eb',
                      border: isSel ? '2px solid #ef4444' : w.emph ? '2px solid #67e8c9' : '1px solid rgba(148, 163, 184, 0.22)',
                      borderLeft: w.br ? '3px solid #26e0c8' : undefined,
                      borderBottom: isLowConf(w, markLowConf) ? '2px solid #FFB020' : undefined,
                      px: 0.5, overflow: 'hidden', cursor: 'grab', zIndex: isSel ? 5 : 2,
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    {/* resize-handles links/rechts */}
                    <Box onMouseDown={(e) => startWordDrag(e, ci, wi, 'l')}
                      sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize' }} />
                    <Box onMouseDown={(e) => startWordDrag(e, ci, wi, 'r')}
                      sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize' }} />
                    <Typography variant="caption" noWrap sx={{ fontWeight: w.emph ? 800 : 500, fontSize: 11.5, pointerEvents: 'none' }}>
                      {w.text}
                    </Typography>
                  </Box>
                );
              }))
            ) : (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: '#64748b' }}>
                <Stack spacing={1} alignItems="center" sx={{ px: 3, textAlign: 'center' }}>
                  <Typography variant="body2">
                    {hasVideo ? 'Nog geen transcriptie. Klik op Spraak herkennen om te starten.' : 'Kies eerst een video.'}
                  </Typography>
                  {uploadStatus === 'uploading' && (
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>Video wordt naar de backend geupload...</Typography>
                  )}
                  {uploadStatus === 'error' && (
                    <Typography variant="caption" sx={{ color: '#fca5a5' }}>
                      Backend-upload mislukt: {uploadError}. Klik op "Upload opnieuw & herken" om opnieuw te proberen.
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {showHint && (
        <Box
          onClick={dismissHint}
          sx={{
            position: 'fixed', inset: 0, zIndex: 2200,
            bgcolor: 'rgba(2, 6, 23, 0.72)', display: 'grid', placeItems: 'center', p: 3,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: 'min(440px, 100%)', bgcolor: '#151a25', borderRadius: 2, p: 3,
              border: '1px solid rgba(148, 163, 184, 0.22)', boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="h6" fontWeight={900}>Welkom bij Ondertitel Studio</Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                Eén editor voor al je ondertitels. Bovenin kies je de stijl — je kunt altijd wisselen:
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#1d2330', border: '1px solid rgba(148,163,184,0.14)' }}>
                  <Typography fontWeight={800} sx={{ color: '#67e8c9' }}>Standaard</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Rustige, leesbare regels — voor broadcast/corporate. Geen woord-voor-woord highlight.
                  </Typography>
                </Box>
                <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#1d2330', border: '1px solid rgba(148,163,184,0.14)' }}>
                  <Typography fontWeight={800} sx={{ color: '#67e8c9' }}>Ritme</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Karaoke: woord-voor-woord highlight met templates — sterk voor social.
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Kies daarnaast een <b>formaat</b> (16:9, 9:16…) en <b>positie</b> rechts in het paneel.
              </Typography>
              <Button
                variant="contained"
                onClick={dismissHint}
                sx={{ background: '#67e8c9', color: '#042f2e', fontWeight: 800, '&:hover': { background: '#5eead4' } }}
              >
                Aan de slag
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Rechtermuisknop-menu op een tijdlijn-woord */}
      <Menu open={!!wordMenu} onClose={() => setWordMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={wordMenu ? { top: wordMenu.y, left: wordMenu.x } : undefined}
        sx={{ zIndex: 3000 }}
        MenuListProps={{ dense: true }}>
        {wordMenu && [
          <MenuItem key="start" onClick={() => { moveWordToEdge(wordMenu.ci, wordMenu.wi, true); setWordMenu(null); }}>↤ Naar begin van regel</MenuItem>,
          <MenuItem key="end" onClick={() => { moveWordToEdge(wordMenu.ci, wordMenu.wi, false); setWordMenu(null); }}>↦ Naar einde van regel</MenuItem>,
          <MenuItem key="left" onClick={() => { swapWordOrder(wordMenu.ci, wordMenu.wi, -1); setWordMenu(null); }}>← Eén plek naar links</MenuItem>,
          <MenuItem key="right" onClick={() => { swapWordOrder(wordMenu.ci, wordMenu.wi, 1); setWordMenu(null); }}>→ Eén plek naar rechts</MenuItem>,
          <MenuItem key="brk" onClick={() => { toggleBreak(wordMenu.ci, wordMenu.wi); setWordMenu(null); }}>↵ Regelafbreking ervoor (Enter)</MenuItem>,
          <MenuItem key="split" onClick={() => { splitAtWord(wordMenu.ci, wordMenu.wi); setWordMenu(null); }}>Splitsen hier</MenuItem>,
          <MenuItem key="iso" onClick={() => { isolateWord(wordMenu.ci, wordMenu.wi); setWordMenu(null); }}>Woord op eigen regel</MenuItem>,
          <MenuItem key="emph" onClick={() => { toggleEmph(wordMenu.ci, wordMenu.wi); setWordMenu(null); }}>Nadruk (bold) aan/uit — E</MenuItem>,
          <MenuItem key="italic" onClick={() => { toggleItalic(wordMenu.ci, wordMenu.wi); setWordMenu(null); }}>Cursief aan/uit — I</MenuItem>,
          <MenuItem key="underline" onClick={() => { toggleUnderline(wordMenu.ci, wordMenu.wi); setWordMenu(null); }}>Onderstreept aan/uit — U</MenuItem>,
          <MenuItem key="del" onClick={() => { deleteWord(wordMenu.ci, wordMenu.wi); setWordMenu(null); }}>Verwijderen</MenuItem>,
        ]}
      </Menu>

      <Dialog open={fontDialogOpen} onClose={() => setFontDialogOpen(false)} maxWidth="xs" fullWidth
        sx={{ zIndex: 3000 }}
        PaperProps={{ sx: { bgcolor: '#16181f', color: '#e5e7eb', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Eigen font toevoegen</DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            autoHighlight
            openOnFocus
            options={fontSuggestions}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              const hits = q ? opts.filter(o => o.name.toLowerCase().includes(q)) : opts;
              return hits.slice(0, 80);
            }}
            inputValue={fontQuery}
            onInputChange={(_, v) => setFontQuery(v)}
            onChange={(_, v) => {
              const name = typeof v === 'string' ? v : v?.name;
              if (name?.trim()) { addCustomFont(name); setFontDialogOpen(false); }
            }}
            renderOption={(props, o) => (
              <li {...props} key={`${o.group}-${o.name}`} style={{ ...props.style, fontFamily: `"${o.name}", sans-serif` }}>{o.name}</li>
            )}
            renderInput={(params) => (
              <TextField {...params} autoFocus placeholder="Begin met typen, bv. Helvetica"
                sx={{ mt: 1, '& .MuiInputBase-root': { color: '#e5e7eb', bgcolor: '#0f1420' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2b3445' } }} />
            )}
            componentsProps={{ popper: { sx: { zIndex: 3100 } } }}
          />
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
            {localFontFamilies
              ? 'Suggesties "Op deze Mac" zijn de fonts op jóúw computer. Voor de render moet het font ook op de render-Mac staan — of upload het .ttf/.otf-bestand.'
              : 'Fonts op deze Mac zoeken werkt in Chrome/Edge na toestemming. Je kunt ook gewoon de exacte naam typen.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFontDialogOpen(false)} sx={{ color: '#94a3b8' }}>Annuleren</Button>
          <Button onClick={confirmFontDialog} disabled={!fontQuery.trim()} variant="contained">Toevoegen</Button>
        </DialogActions>
      </Dialog>

      {/* Per-regel positie: doorzetten naar andere regels */}
      <Menu open={!!posMenu} onClose={() => setPosMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={posMenu ? { top: posMenu.y, left: posMenu.x } : undefined}
        sx={{ zIndex: 3000 }}
        MenuListProps={{ dense: true }}>
        {posMenu && (hasOwnCaptionPos(captions[posMenu.ci]) ? [
          <MenuItem key="next" onClick={() => { applyCaptionPos(posMenu.ci, 'next'); setPosMenu(null); }}>↓ Doorzetten naar volgende regel</MenuItem>,
          <MenuItem key="block" onClick={() => { applyCaptionPos(posMenu.ci, 'block'); setPosMenu(null); }}>⇣ Doorzetten tot de volgende eigen positie</MenuItem>,
          <MenuItem key="all" onClick={() => { applyCaptionPos(posMenu.ci, 'all'); setPosMenu(null); }}>⇅ Doorzetten naar alle regels</MenuItem>,
          <MenuItem key="default" onClick={() => { makeCaptionPosDefault(posMenu.ci); setPosMenu(null); }}>★ Als standaardpositie instellen</MenuItem>,
          <Divider key="div" />,
          <MenuItem key="reset" onClick={() => { resetCaptionPos(posMenu.ci); setPosMenu(null); }}>× Terug naar standaardpositie</MenuItem>,
        ] : [
          <MenuItem key="prev" onClick={() => { copyPrevCaptionPos(posMenu.ci); setPosMenu(null); }}>↑ Positie van vorige eigen regel overnemen</MenuItem>,
        ])}
      </Menu>

      <Dialog open={showHelp} onClose={() => setShowHelp(false)} maxWidth="md" fullWidth
        sx={{ zIndex: 3000 }}
        PaperProps={{ sx: { bgcolor: '#16181f', color: '#e5e7eb', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800 }}>
          <HelpOutlineIcon /> Ondertitel Studio — handboek
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setShowHelp(false)} sx={{ color: '#94a3b8' }}>Sluiten</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(148,163,184,.18)',
          '& h3': { color: '#e5e7eb', fontSize: 14, fontWeight: 800, mt: 2.5, mb: 0.5, borderBottom: '1px solid rgba(148,163,184,.18)', pb: 0.5 },
          '& h3:first-of-type': { mt: 1 },
          '& b': { color: '#e5e7eb' }, '& li': { mb: 0.4, color: '#cbd5e1' }, '& p': { color: '#cbd5e1', my: 0.6, fontSize: 13 },
          '& ul, & ol': { pl: 2.5, my: 0.5 } }}>
          <Typography variant="body2" component="h3">Wat is RITMO?</Typography>
          <Typography component="p">Een tool die spraak in je video herkent en er <b>ritmische/karaoke-ondertitels</b> van maakt: het gesproken woord licht mee op. Je stuurt de tekst, timing en stijl, en exporteert naar ProRes 4444 (ingebrand of als losse titellaag), FCPXML, of losse ondertitelbestanden (SRT/VTT).</Typography>

          <Typography variant="body2" component="h3">Snel starten</Typography>
          <Box component="ol">
            <li><b>Importeer</b> een video (sleep hem in beeld of klik). Elk formaat mag (9:16, 1:1, 4:5, 16:9).</li>
            <li>Klik <b>Spraak herkennen</b> — je krijgt per woord een tijdsblok.</li>
            <li>Kies een look in <b>Templates</b> en finetune in <b>Opmaak</b> (font, kleuren, grootte, positie).</li>
            <li>Corrigeer woorden/timing in <b>Tekst</b> en de <b>tijdlijn</b>.</li>
            <li><b>Exporteer</b> (Export-tab).</li>
          </Box>

          <Typography variant="body2" component="h3">De tabbladen (rechts)</Typography>
          <Box component="ul">
            <li><b>Templates</b> — preset + 30 animatiestijlen (per categorie).</li>
            <li><b>Opmaak</b> — lettertype (Bold/Cursief/Onderstreept), kleuren (incl. Nadruk), grootte, horizontale/verticale positie, woorden per regel, rand, hoofdletters, en "geen pop/beweging".</li>
            <li><b>Tekst</b> — transcript corrigeren, taal/vertaling, spelling- en grammaticacontrole.</li>
            <li><b>Eigen positie per regel</b> — sleep een regel in de preview. Via rechterklik (of de chip 📍 in de regellijst) zet je die positie door naar de volgende regel, tot de volgende eigen positie, naar alle regels, of maak je er de standaard van.</li>
            <li><b>Export</b> — ProRes 4444 (incl. alfa-only + fragment + snelle render), FCPXML en SRT/VTT.</li>
          </Box>

          <Typography variant="body2" component="h3">Tijdlijn</Typography>
          <Box component="ul">
            <li>Sleep een woordblok om het te <b>verplaatsen</b>; sleep de randen om de <b>timing</b> aan te passen.</li>
            <li>Klik op de balk om te <b>scrubben</b>. <b>Dubbelklik op een lege plek</b> = woord toevoegen.</li>
            <li><b>Rechterklik</b> op een woord voor het menu (begin/einde regel, splitsen, eigen regel, nadruk, verwijderen…).</li>
            <li>De <b>waveform</b> (golfvorm) helpt je woorden op de spraak te leggen.</li>
          </Box>

          <Typography variant="body2" component="h3">Sneltoetsen</Typography>
          <Table size="small" sx={{ '& td': { borderColor: 'rgba(148,163,184,.14)', color: '#cbd5e1', py: 0.5 }, '& td:first-of-type': { fontFamily: 'monospace', color: '#e5e7eb', width: '34%', whiteSpace: 'nowrap' } }}>
            <TableBody>
              {[
                ['Spatie', 'Afspelen / pauze'],
                ['J / K / L', 'Achteruit / pauze / vooruit (L herhaald = sneller: 2×, 4×, 8×)'],
                ['⌘Z / ⌘⇧Z', 'Ongedaan maken / opnieuw'],
                ['Backspace / Delete', 'Geselecteerd woord verwijderen (tijdlijn)'],
                ['E', 'Geselecteerd woord benadrukken (punchword)'],
                ['S', 'Regel splitsen vanaf het geselecteerde woord'],
                ['Enter', 'Regelafbreking vóór het geselecteerde woord'],
                ['← / →', 'Geselecteerd woord verschuiven (Shift = grotere stap · ⌥ = einde/lengte)'],
                ['+ / −', 'Tijdlijn in-/uitzoomen'],
                ['Home / End', 'Naar het begin / einde van de tijdlijn'],
                ['H', 'Hand-tool (pannen)'],
                ['I / O', 'In-punt / uit-punt zetten (voor "Alleen fragment exporteren")'],
              ].map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell>{v}</TableCell></TableRow>)}
            </TableBody>
          </Table>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
            "Geselecteerd woord" = aangeklikt blok in de tijdlijn (rode rand). Sneltoetsen werken niet terwijl je in een tekstveld typt.
          </Typography>

          <Typography variant="body2" component="h3">Exporteren</Typography>
          <Box component="ul">
            <li><b>ProRes 4444</b> — ingebrande ondertitels in maximale kwaliteit. Opties: alleen ondertitels (transparante alfa), alleen fragment (in/uit-tijd), snelle render (lagere resolutie om te testen).</li>
            <li><b>FCPXML</b> — open in Final Cut Pro X om tekst/timing te finetunen.</li>
            <li><b>SRT / VTT</b> — losse ondertitelbestanden.</li>
          </Box>

          <Typography variant="body2" component="h3">Afkortingen</Typography>
          <Table size="small" sx={{ '& td': { borderColor: 'rgba(148,163,184,.14)', color: '#cbd5e1', py: 0.5 }, '& td:first-of-type': { fontFamily: 'monospace', color: '#e5e7eb', width: '30%', whiteSpace: 'nowrap' } }}>
            <TableBody>
              {[
                ['CPS', 'Characters Per Second — leessnelheid per regel. Groen ≤17 · oranje ≤23 · rood = te snel.'],
                ['ProRes 4444', 'Hoge-kwaliteit Apple-codec; "4444" kan een alfakanaal (transparantie) bevatten.'],
                ['Alfa / alpha', 'Transparantiekanaal — een ondertitellaag zonder achtergrond om over je edit te leggen.'],
                ['SRT / VTT', 'Standaard ondertitelbestanden (YouTube, social, broadcast).'],
                ['Veilige zone', 'Marge waarbinnen je tekst houdt zodat platform-UI (TikTok/Reels/…) hem niet bedekt. Alleen preview.'],
              ].map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell>{v}</TableCell></TableRow>)}
            </TableBody>
          </Table>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1.5 }}>
            Nog niet gekoppeld in de interne versie: meertalig/DeepL-vertaling, grammaticacontrole (LanguageTool), en het kiezen van een lokale opslagmap.
          </Typography>
        </DialogContent>
      </Dialog>

      {notice && (
        <Box sx={{ position: 'fixed', right: 18, bottom: 18, zIndex: 2100, width: 360, maxWidth: 'calc(100vw - 36px)' }}>
          <Alert
            severity={notice.severity}
            onClose={() => setNotice(null)}
            sx={{ boxShadow: '0 18px 60px rgba(0,0,0,0.35)' }}
          >
            {notice.message}
          </Alert>
        </Box>
      )}
    </Box>
  );
}
